/**
 * Milano Wigs — PDP Wizard Overlay v1.5.5
 * Runs AFTER personalize-options.js
 *
 * CHANGE (v1.5.5): SECURE-METHOD POPUP MISROUTE FIX
 * - FIX: getCollectionSignal() was including document.referrer and breadcrumb
 *   sources when building the signal string used by resolveStepHelpId().
 *   When a user navigated to a wig PDP from /collections/wigs-toppers (the
 *   main nav landing for the combined wigs+toppers section), the substring
 *   "toppers" in the referrer URL leaked into the signal and matched the
 *   /topper/ pattern in SECURE_POPUP_MAP, incorrectly routing wig customers
 *   to the topper-specific #dtoppers-secure-method popup.
 * - Removed: document.referrer + breadcrumb hrefs/texts from signal builder.
 *   The current page's pathname + product title are sufficient signals —
 *   a real Divine Topper PDP has "topper" in both its URL handle and its
 *   <h1>, so the routing still works correctly for actual toppers.
 * - Hardened: SECURE_POPUP_MAP /topper/ pattern now uses a word boundary
 *   and a negative lookahead `\btopper(?!s\b)/i` so the plural collection
 *   name "toppers" can't match even if it leaks in from another source.
 *
 * CHANGE (v1.5.4-sale):
 * - ADDED: "You save $X" badge in review card — warm gradient card with
 *   tag icon, replaces the flat "X% off applied" text. Dollar amount
 *   computed from fullPrice - currentTotal for accuracy.
 * - ADDED: overlayDiscountedPrices() — shows strikethrough + discounted
 *   amount on individual option price labels when DN ratio is active.
 *   "Full Rooting $165" → "~~$165~~ $132". Reversible via data-mw-orig-text.
 *
 * CHANGE (v1.5.4):
 * - FIX: getActualServicesDollars() — reads mw_dn_ratio from sessionStorage
 *   instead of ATC button text (hidden since v2026-05-07a). Services toggle,
 *   wizard total, and price context now show correct discounted amounts.
 * - FIX: updateReviewCard() — reads mw_dn_ratio from sessionStorage. Uses
 *   SC Options element as single source of truth for total price (matches
 *   computeFinalPrice in personalize-options.js), fixing price mismatch
 *   caused by conditional sub-options (Face Framing, Bangs) not captured
 *   by getServicesDollars() reconstruction. Review card total now equals
 *   cart bar total exactly.
 *
 * CHANGE (v1.5.3):
 * - FIX: Size chart popup scroll freeze (proper fix). The issue is a stale
 *   touchmove preventDefault listener from Palo Alto's Size Guide drawer
 *   that doesn't get removed on close. Capture-phase touchmove handler
 *   with stopImmediatePropagation() only fires when overflow:hidden is
 *   stuck AND nothing is actually open — no intervention during normal
 *   scrolling (avoids breaking third-party swipe gestures). 89→43 lines.
 * - FIX: Services price context (+$X services) positioned as inline span
 *   directly after price element. Stays on same line as price on all
 *   device sizes instead of wrapping below Splitit.
 * - ADDED: Full-card click targets — clicking anywhere on an option card
 *   (not just the label/input) selects it. Delegated listener.
 * - ADDED: Flat options mode for single-step products (STL sets). Options
 *   shown directly without accordion headers. Toggle renamed to
 *   "Personalize This Wig/Topper" with dynamic subtitle.
 * - ADDED: Step-level reassurance notice above Continue button when paid
 *   Color/Cut options selected. Replaces per-option follow-up notices.
 * - CHANGED: Toggle copy "Building Your Personalized" → "Personalizing
 *   Your" (fits one line on mobile). Subtitles shortened.
 * - CHANGED: Mobile hint layout — help link moves below description.
 * - REMOVED: Dead code (injectSpecTags, injectCustomerQuote,
 *   injectTrustStrip, injectOptionDescriptions, OPTION_DESCS). -184 lines.
 *
 * CHANGE (v1.5.2):
 * - FIX: (superseded by v1.5.3) Attempted overflow:hidden safety net —
 *   didn't address the actual root cause (stale touchmove listener).
 *
 * CHANGE (v1.5.1):
 * - ADDED: Minimum step threshold — wizard only activates when 2+ steps
 *   exist. Single-step products (e.g., STL sets with only Secure Method)
 *   fall back to accordion mode, avoiding unnecessary wizard UI friction.
 *
 * CHANGE (v1.5.0):
 * - ADDED: "All optional" reassurance line below step pills. Communicates
 *   upfront that no step requires a selection — wig ships as-pictured.
 * - ADDED: "Keep as-pictured →" express exit link below Continue button.
 *   Jumps straight to review with current defaults. Hidden on last step
 *   (Review My Wig button already serves that purpose). Reduces perceived
 *   pressure to fill every step — users can exit the wizard at any point.
 * - FIX: autoSelectRecommended fallback scoped to secure method rows only.
 *   Style, Part Location, Cut stay unselected unless they have skip options.
 *
 * CHANGE (v1.4.9):
 * - ADDED: autoSelectRecommended() — pre-selects options with a
 *   "Recommended" badge when nothing else is selected in their group.
 *   PRICE-SAFE: skips any option with price > $0. Falls back to
 *   selecting the first $0 radio option in unselected rows (e.g.,
 *   "Clips & Combs" when "Clips + WiGrip" is priced). Only targets
 *   radio groups — checkbox groups (color) left to autoSelectDefaults.
 * - SHORTENED: Ear Pieces "Yes" description from 14 words to 8:
 *   "For thin or no bio hair near the ears" — fits one line at 430px.
 *   Full explanation lives in the ear-pieces popup.
 *
 * CHANGE (v1.4.8):
 * - ADDED: Addon headers — config-driven section headers for secondary/
 *   standalone options within a wizard step. ADDON_HEADERS config array
 *   drives injection: each entry specifies nameMatch, title, description,
 *   optional help link, and warm/default treatment. Future standalone
 *   options just need a config entry — no structural code changes.
 * - ADDED: Ear Pieces addon header ("Add Ear Pieces" + context desc).
 * - ADDED: Front Hairline addon header with warm card treatment ("About
 *   your front hairline" + empathetic description). Warm variant uses
 *   left accent border + warm background for sensitive hair loss context.
 * - ADDED: Front Hairline "Yes" and "No" value descriptions:
 *   "Yes" → "I have bio hair at my front hairline to blend"
 *   "No" → "My front hairline is thinning or has receded"
 *   "Undecided" → "We'll reach out to discuss what works best for you"
 *
 * CHANGE (v1.4.7):
 * - CHANGED: All badges from "Most chosen" → "Recommended".
 * - MERGED: Wig secure + topper secure badge conditions into one block.
 * - ADDED: Step group separators (mw-step-group-sep) between option groups.
 * - FIX: Ear Pieces "Yes" description no longer requires "Suggested" in text.
 *
 * CHANGE (v1.4.6):
 * - ADDED: Topper secure "Clips + TopGrip" badge.
 * - ENHANCED: Ear Pieces option handling:
 *   "No" → skip card (mw-no-ear) + "Recommended" badge via JS.
 *   "Yes - Suggested..." → short "Yes" label + description text below.
 *   Detection scoped to ear-row name, not label text — works before
 *   and after admin renames. Uses data-mw-ear attribute for idempotency.
 *
 * CHANGE (v1.4.5):
 * - FIX: Removed /wavy/i from SECURE_POPUP_MAP. "Wavy" is a hair texture,
 *   not a construction type — it doesn't change how you secure the wig or
 *   topper. All products now fall to their collection-specific secure popup
 *   (onyx, diamond, topaz/emerald, topper) or the generic secure-method
 *   fallback. Wavy Fringe Toppers correctly get dtoppers-secure-method via
 *   the /topper/i match. Wavy wigs get generic secure-method.
 *   The wavy-secure-method popup block remains in the theme editor for use
 *   in the Style step (Step 3) alongside Please Style My Wig options.
 * - ADDED: INLINE_HELP_MAP entry for "Bangs or Fringe" sub-gate option
 *   pointing to bangs-or-fringe popup. Pill only renders if the popup block
 *   exists in the theme editor — safe to deploy before the block is created.
 * - ADDED: autoSelectDefaults() auto-checks "As Is" / "No Cut Service" /
 *   "No Color Service" options on init, giving every step a sensible default.
 *   Only fires when nothing is already selected in that option group. Change
 *   events dispatch so wizard completion states, totals, and review card
 *   all update correctly. Color exclusivity logic handles mutual uncheck.
 * - ADDED: Style step "As-pictured" separator — same pattern as Cut step
 *   (mw-no-style class, move to top, compact separator "Or choose a style:")
 *   but only renders when the option set includes an "As Is" value.
 *   Wavy-only sets (Air Dried / Blow Dried / Beach Waved) have no skip
 *   option, so no separator appears — clean fallback.
 * - ADDED: Follow-up notices for hands-on services (Custom Color $525,
 *   Personalized Overall Cut $175). Inline message with envelope icon
 *   appears below the option when selected, hides when deselected.
 *   Tells customer a Milano specialist will email them post-order.
 *   FOLLOWUP_NOTICES config maps option labels → messages.
 *
 * CHANGE (v1.4.4):
 * - FIX: Conditional sub-gate options (Face Framing position, Bangs style)
 *   now get their enhancements (sub-gate labels, inline help links, label
 *   renames, price formatting) when they become visible, not just at init.
 *   SC Options shows/hides these AFTER a parent option is selected.
 *   Fix: re-run all idempotent enhancement functions (applyLabelRenames,
 *   injectBadgesAndLabels, injectInlineHelp, formatWizardPrices) on change
 *   events with 200ms delay. Fixes "As Is" → "As-pictured" rename in Style
 *   step and Face Framing "? View" help link not appearing.
 *
 * CHANGE (v1.4.3):
 * - Collection-aware step help popups for Secure (step 0), Cut (step 2),
 *   and Styling (step 3). Step 0 now opens the correct secure-method
 *   popup per collection (onyx-secure-method, diamond-secure-method, etc.)
 *   instead of always opening the generic secure-method.
 * - Added SECURE_POPUP_MAP, STYLE_POPUP_MAP, CUT_POPUP_MAP configs.
 * - Added INLINE_HELP_MAP + injectInlineHelp() for sub-gate options:
 *   "Position of Face Framing" → face-farming-position popup
 *   "Location of Part" → location-of-part popup
 *   Small "? View" pill button appears next to sub-gate labels.
 * - Added resolveStepHelpId() with getCollectionSignal() for context
 *   detection (option names + URL + breadcrumbs + product title).
 * - Verified all popup container IDs exist before injecting links.
 *
 * CHANGE (v1.4.1):
 * - REMOVED lace front label renaming (Zig Zag Standard Lace stays as-is per Dawn).
 *   Subtitles still inject underneath. No admin rename needed.
 * - ADDED LABEL_RENAMES config for SC Options storefront-only renames:
 *   "No Color Service" → "Keep Original Color"
 *   "As Is" → "No Cut — keep as pictured"
 *   Order line item properties remain unchanged (original SC Options values submit).
 * - Added applyLabelRenames() function called during enhanceFormElements().
 */

(function() {
  'use strict';

  if (window.MilanoWizardInit) return;
  window.MilanoWizardInit = true;

  var VERSION = 'v1.5.5';
  console.log('[Milano Wizard] ' + VERSION + ' loaded');

  if (window.location.pathname.indexOf('/products/') === -1) return;

  // =========================================================================
  // CONFIG
  // =========================================================================

  var STEP_DEFS = [
    { id: 0, label: 'Secure', patterns: [/secure method/i, /ear piece/i, /blending.*hairline/i, /front hairline/i], hint: 'How should we secure your wig?', subhint: 'Choose how your wig stays comfortably in place.', helpId: 'secure-method', helpText: 'See options' },
    { id: 1, label: 'Color', patterns: [/color service/i, /add color/i], hint: 'Would you like color services?', subhint: 'Add a natural-looking root, highlights, or custom blend. Options vary by wig.', helpId: 'color-service', helpText: 'See color examples' },
    { id: 2, label: 'Cut', patterns: [/personalize your cut/i, /choose your cut/i, /signature.*cut/i, /face fram/i, /bangs or fringe/i, /position of/i], hint: 'Would you like a custom cut?', subhint: 'Our stylists shape each wig by hand to frame your face.', helpId: 'milano-signature', helpText: 'See cut options' },
    { id: 3, label: 'Style', patterns: [/style my/i, /please style/i, /location of part/i], hint: 'How should we style it?', subhint: 'Your preferred styling and part location.', helpId: null, helpText: null }
  ];

  var SECURE_POPUP_MAP = [
    { pattern: /onyx/i,                popup: 'onyx-secure-method' },
    { pattern: /diamond/i,             popup: 'diamond-secure-method' },
    { pattern: /topaz|emerald/i,       popup: 'topeme-secure-method' },
    // \btopper(?!s\b) — match "topper" as a whole word, but reject the
    // plural "toppers" (which appears in the /collections/wigs-toppers
    // collection name and would otherwise misroute wig customers to the
    // topper popup). See v1.5.5 changelog.
    { pattern: /\btopper(?!s\b)/i,     popup: 'dtoppers-secure-method' }
  ];

  var STYLE_POPUP_MAP = [
    { pattern: /wavy/i, popup: 'please-style-my-wig-wavy' }
  ];

  var CUT_POPUP_MAP = [
    { pattern: /signature.*cut/i, popup: 'milano-signature' }
  ];

  var LACE_POPUP_MAP = [
    { pattern: /ponytail/i, popup: 'lace-compare-ponytail' },
    { pattern: /divine/i,   popup: 'lace-compare-divine' }
  ];

  var INLINE_HELP_MAP = [
    { nameMatch: 'position of', popup: 'face-farming-position', label: 'View' },
    { nameMatch: 'location of part', popup: 'location-of-part', label: 'View' },
    { nameMatch: 'bangs or fringe', popup: 'bangs-or-fringe', label: 'View' },
    { nameMatch: 'style my', popup: 'please-style-my-wig', label: 'View' },
    { nameMatch: 'please style', popup: 'please-style-my-wig', label: 'View' }
  ];

  var LACE_SUBS = {
    'zig zag standard lace': 'Flatter look, great for bangs',
    'zig zag lace': 'Flatter look, great for bangs',
    'standard lace': 'Flatter look, great for bangs',
    'standard': 'Flatter look, great for bangs',
    'zig zag deep lace': 'More volume, ideal for side parts',
    'deep lace': 'More volume, ideal for side parts'
  };

  var LABEL_RENAMES = {
    'no color service': 'No Color \u2014 keep as pictured',
    'keep original color': 'No Color \u2014 keep as pictured',
    'as is': 'As-pictured'
  };

  // Step-level reassurance — shown above Continue when paid options selected.
  // lead: bold intro (Color = exploratory, Cut = decisive completion signal)
  // body: follow-up details
  // reviewLabel: short label for review card reminder
  var STEP_REASSURANCE = {
    1: {
      lead: 'We\u2019ll bring your vision to life.',
      body: 'After your order, our team will email you to discuss your color preferences \u2014 reference photos welcome.',
      reviewLabel: 'color'
    },
    2: {
      lead: 'We\u2019ll shape it just for you.',
      body: 'After your order, our team will email you to discuss your desired cut and face shape.',
      reviewLabel: 'cut'
    }
  };

  var ADDON_HEADERS = [
    {
      nameMatch: 'ear piece',
      title: 'Add Ear Pieces',
      desc: 'Small hair sections near the ears for seamless coverage.',
      helpId: 'ear-pieces',
      helpText: 'Learn more',
      warm: false
    },
    {
      nameMatch: 'hairline',
      altMatch: 'blending',
      title: 'About your front hairline',
      desc: 'This helps us prepare your topper for the most natural-looking result.',
      helpId: 'front-hairline',
      helpText: 'Learn more',
      warm: true
    }
  ];

  var CAP_SIZE_MAP = {
    'princess': { size: 'small' },
    'processed euro': { size: 'large' },
    'processed-euro': { size: 'large' }
  };

  // =========================================================================
  // STATE
  // =========================================================================

  var wiz = {
    active: false,
    currentStep: 0,
    steps: [],
    reviewed: false,
    indicatorEl: null,
    navEl: null,
    hintEl: null,
    subhintEl: null,
    toggleTotalEl: null,
    wizTotalEl: null,
    reviewCardEl: null,
    stepNoticeEl: null,
    intervals: [],
    observers: [],
    collectionSignal: '',
    isTopper: false
  };

  // =========================================================================
  // HELPERS
  // =========================================================================

  function stripPriceText(text) {
    return (text || '').replace(/\s*\+?\$[\d,.]+\s*(usd)?/gi, '').trim().toLowerCase();
  }

  function getServicesDollars() {
    var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
    if (checkbox && !checkbox.checked) return 0;

    // SC Options element is the single source of truth (includes conditional
    // sub-options like Face Framing that the summary might miss).
    // Same source as computeFinalPrice() in personalize-options.js.
    var fw = document.querySelector('product-form[data-form-wrapper]');
    if (fw) {
      var baseCents = parseInt(fw.getAttribute('data-variant-price') || '0', 10);
      var scEl = document.querySelector('[data-product-price].bold_option_price_display');
      if (scEl) {
        var scText = (scEl.textContent || '').replace(/[^\d.]/g, '');
        var scCents = Math.round((parseFloat(scText) || 0) * 100);
        var added = scCents - baseCents;
        if (added > 50) return Math.round(added / 100);
      }
    }

    // Fallback: summary total (may miss conditional sub-options)
    var summaryTotal = document.querySelector('.milano-summary__total span:last-child');
    if (summaryTotal) {
      var st = (summaryTotal.textContent || '').replace(/[^\d.]/g, '');
      var summaryDollars = parseFloat(st) || 0;
      return Math.round(summaryDollars);
    }

    return 0;
  }

  function walkUp(el, cls) {
    var node = el ? el.parentElement : null;
    while (node && !node.classList.contains(cls)) node = node.parentElement;
    return node;
  }

  function detectCollection() {
    var collectionEls = document.querySelectorAll('[data-collection-handle], .breadcrumb a, .breadcrumbs a');
    var pageUrl = window.location.pathname.toLowerCase();
    var productTitle = (document.querySelector('.product__title, h1') || {}).textContent || '';
    var combined = pageUrl + ' ' + productTitle.toLowerCase();

    for (var c = 0; c < collectionEls.length; c++) {
      combined += ' ' + (collectionEls[c].getAttribute('href') || '') + ' ' + (collectionEls[c].textContent || '');
    }

    if (document.referrer) combined += ' ' + document.referrer.toLowerCase();

    for (var key in CAP_SIZE_MAP) {
      if (combined.indexOf(key) > -1) return key;
    }
    return null;
  }

  function getCollectionSignal(step) {
    var parts = [];
    if (step && step.rows) {
      for (var r = 0; r < step.rows.length; r++) {
        parts.push(step.rows[r].getAttribute('data-option-name') || '');
      }
    }

    if (!wiz.collectionSignal) {
      var pageParts = [];
      pageParts.push(window.location.pathname);
      pageParts.push((document.querySelector('.product__title, h1') || {}).textContent || '');
      // NOTE: document.referrer and breadcrumb sources intentionally excluded.
      // Both leak parent collection names (e.g. "wigs-toppers", "Wigs & Toppers")
      // into the signal, falsely matching SECURE_POPUP_MAP patterns. The current
      // product's pathname + title are sufficient to identify product type
      // (a real Divine Topper PDP has "topper" in both). See v1.5.5 changelog.
      wiz.collectionSignal = pageParts.join(' ').toLowerCase();
    }

    return (parts.join(' ') + ' ' + wiz.collectionSignal).toLowerCase();
  }

  function resolveStepHelpId(step) {
    var defaultId = step.helpId || null;
    if (!defaultId) return null;

    var map = null;
    if (step.id === 0) map = SECURE_POPUP_MAP;
    else if (step.id === 2) map = CUT_POPUP_MAP;
    else if (step.id === 3) map = STYLE_POPUP_MAP;

    if (!map) {
      return document.getElementById(defaultId) ? defaultId : null;
    }

    var signal = getCollectionSignal(step);

    for (var i = 0; i < map.length; i++) {
      if (map[i].pattern.test(signal) && document.getElementById(map[i].popup)) {
        console.log('[Milano Wizard] Step ' + step.id + ' help \u2192 #' + map[i].popup + ' (collection-aware)');
        return map[i].popup;
      }
    }

    return document.getElementById(defaultId) ? defaultId : null;
  }

  // =========================================================================
  // INIT
  // =========================================================================

  var _scrollFixInstalled = false;

  function initScrollFix() {
    if (_scrollFixInstalled) return;
    _scrollFixInstalled = true;

    // -----------------------------------------------------------------------
    // SIZE CHART SCROLL FREEZE FIX
    //
    // Palo Alto's size chart drawer adds a touchmove preventDefault listener
    // that isn't removed on close, leaving scroll frozen on mobile.
    //
    // Fix: capture-phase touchmove handler that only fires when scroll IS
    // stuck (overflow:hidden on body) AND nothing is actually open (no
    // drawer, popup, or modal). This avoids the old approach of killing
    // ALL touchmove listeners during normal scrolling, which broke
    // third-party swipe gestures (Gorgias, Okendo, etc).
    // -----------------------------------------------------------------------

    function isAnythingOpen() {
      var cl = document.body.classList;
      if (cl.contains('js-drawer-open') || cl.contains('js-drawer-open-cart') || cl.contains('mw-popup-open')) return true;
      return !!document.querySelector('.pop-up-container[style*="flex"],.pswp--open');
    }

    document.addEventListener('touchmove', function(e) {
      if (document.body.style.overflow === 'hidden' && !isAnythingOpen()) {
        e.stopImmediatePropagation();
      }
    }, { capture: true, passive: false });

    // Safety net: clear stuck mw-popup-open class + overflow
    setInterval(function() {
      if (document.body.classList.contains('mw-popup-open')) {
        if (!document.querySelector('.pop-up-container[style*="flex"]')) {
          document.body.classList.remove('mw-popup-open');
          document.body.style.overflow = '';
        }
      }
      // Clear stuck overflow when nothing is open (catches size chart too)
      if (document.body.style.overflow === 'hidden' && !isAnythingOpen()) {
        document.body.style.overflow = '';
      }
    }, 3000);
  }

  function init() {
    initScrollFix();

    if (!document.body.classList.contains('milano-grace')) {
      var path = window.location.pathname.toLowerCase();
      if (path.indexOf('/products/grace') !== -1) {
        document.body.classList.add('milano-grace');
        console.log('[Milano Wizard] Grace detected via URL fallback');
      }
    }

    var attempts = 0;
    var poll = setInterval(function() {
      attempts++;
      var panel = document.querySelector('.milano-panel');
      var content = panel ? panel.querySelector('.milano-panel__content') : null;
      var rows = content ? content.querySelectorAll('.milano-option-row') : [];
      if (rows.length > 0) {
        clearInterval(poll);
        try { transform(panel, content, rows); }
        catch (e) { console.error('[Milano Wizard] Transform failed:', e); cleanup(); }
      } else if (attempts >= 60) { clearInterval(poll); }
    }, 500);
  }

  // =========================================================================
  // TRANSFORM
  // =========================================================================

  function transform(panel, content, rows) {
    var stepMap = {}, i, s;
    for (i = 0; i < rows.length; i++) {
      var optName = rows[i].getAttribute('data-option-name') || '';
      var stepId = classifyRow(optName);
      if (!stepMap[stepId]) {
        var def = STEP_DEFS[stepId] || { id: stepId, label: 'Options', hint: '', subhint: '' };
        stepMap[stepId] = { id: stepId, label: def.label, hint: def.hint, subhint: def.subhint, helpId: def.helpId || null, helpText: def.helpText || null, rows: [], done: false };
      }
      stepMap[stepId].rows.push(rows[i]);
    }
    var stepIds = Object.keys(stepMap).map(Number).sort();
    for (s = 0; s < stepIds.length; s++) wiz.steps.push(stepMap[stepIds[s]]);
    if (wiz.steps.length === 0) return;

    // Detect topper products by option names (needed for copy below)
    for (i = 0; i < rows.length; i++) {
      var rn = (rows[i].getAttribute('data-option-name') || '').toLowerCase();
      if (rn.indexOf('topper') > -1 || rn.indexOf('topgrip') > -1) { wiz.isTopper = true; break; }
    }

    // Always rename toggle title — "Customize" → "Personalize" on all products
    renameToggleTitle();

    // Single-step products (STL, simple sets): show options flat, no accordion.
    // Wizard (2+ steps) adds step pills, nav, and full overlay UX.
    // Accordion stays as fallback only if wizard transform() throws.
    if (wiz.steps.length < 2) {
      console.log('[Milano Wizard] ' + wiz.steps.length + ' step — flat options mode');
      flattenOptions(content, rows);
      return;
    }

    document.body.classList.add('milano-wizard-mode');

    // Inject dynamic wizard styles (mobile hint reorder + step notice)
    if (!document.getElementById('mw-mobile-styles')) {
      var mobileStyle = document.createElement('style');
      mobileStyle.id = 'mw-mobile-styles';
      mobileStyle.textContent =
        // Step reassurance notice — appears above Continue button
        '.mw-step-notice {' +
          'display: flex; align-items: flex-start; gap: 10px;' +
          'padding: 12px 14px; margin-top: 16px;' +
          'background: var(--mw-accent-faint, #faf9f7);' +
          'border: 1px solid var(--mw-border, #E3DCD4);' +
          'border-radius: var(--mw-radius, 10px);' +
          'font-size: 13px; line-height: 1.5;' +
          'color: var(--mw-brown-med, #6B5E55);' +
          'animation: mwFadeIn 0.3s ease;' +
        '}' +
        '.mw-step-notice__icon {' +
          'flex-shrink: 0; margin-top: 2px;' +
          'color: var(--mw-accent, #8b7355);' +
        '}' +
        '.mw-step-notice__text { flex: 1; }' +
        '.mw-step-notice__text strong {' +
          'font-weight: 600; color: var(--mw-brown, #2D2926);' +
        '}' +
        // Review card reminder
        '.mw-review-card__reminder {' +
          'font-size: 12px; line-height: 1.5;' +
          'color: var(--mw-brown-light, #9A8E84);' +
          'margin-top: 10px; padding-top: 8px;' +
          'border-top: 1px solid var(--mw-border, #E3DCD4);' +
        '}' +
        // Mobile: reorder hint row (help link below description)
        '@media screen and (max-width: 768px) {' +
          '.mw-hint-wrap { display: flex; flex-direction: column; }' +
          '.mw-hint-row { display: contents; }' +
          '.mw-hint { order: 1; }' +
          '.mw-subhint { order: 2; }' +
          '.mw-step-help { order: 3; align-self: flex-start; margin-top: 8px; }' +
          '.mw-step-notice { font-size: 12px; padding: 10px 12px; margin-top: 12px; gap: 8px; }' +
        '}';
      document.head.appendChild(mobileStyle);
    }

    var indicatorRow = document.createElement('div');
    indicatorRow.className = 'mw-indicator-row';

    var indicator = document.createElement('div');
    indicator.className = 'mw-indicator';
    indicator.addEventListener('click', function(e) {
      var pill = e.target;
      while (pill && !pill.classList.contains('mw-pill')) pill = pill.parentElement;
      if (!pill) return;
      e.preventDefault();
      goToStep(parseInt(pill.getAttribute('data-step'), 10));
    });
    wiz.indicatorEl = indicator;

    var wizTotal = document.createElement('div');
    wizTotal.className = 'mw-wiz-total';
    wizTotal.style.display = 'none';
    wiz.wizTotalEl = wizTotal;

    indicatorRow.appendChild(indicator);
    indicatorRow.appendChild(wizTotal);
    content.insertBefore(indicatorRow, content.firstChild);

    var hintWrap = document.createElement('div');
    hintWrap.className = 'mw-hint-wrap';
    var hint = document.createElement('div');
    hint.className = 'mw-hint';
    var subhint = document.createElement('div');
    subhint.className = 'mw-subhint';
    var stepHelp = document.createElement('button');
    stepHelp.type = 'button';
    stepHelp.className = 'mw-step-help';
    stepHelp.style.display = 'none';
    stepHelp.addEventListener('click', function(e) {
      e.preventDefault();
      var popupId = stepHelp.getAttribute('data-popup-id');
      if (popupId && window.showSecurePopup) {
        window.showSecurePopup(popupId);
      } else if (popupId) {
        var container = document.getElementById(popupId);
        if (container) {
          container.style.display = 'flex';
          document.body.classList.add('mw-popup-open');
        }
      }
    });
    var hintRow = document.createElement('div');
    hintRow.className = 'mw-hint-row';
    hintRow.appendChild(hint);
    hintRow.appendChild(stepHelp);
    hintWrap.appendChild(hintRow);
    hintWrap.appendChild(subhint);
    wiz.hintEl = hint;
    wiz.subhintEl = subhint;
    wiz.stepHelpEl = stepHelp;
    indicatorRow.insertAdjacentElement('afterend', hintWrap);

    // Step-level reassurance notice — shown above Continue when paid
    // Color/Cut options are selected. Single message per step.
    var stepNotice = document.createElement('div');
    stepNotice.className = 'mw-step-notice';
    stepNotice.style.display = 'none';
    stepNotice.setAttribute('aria-live', 'polite');
    wiz.stepNoticeEl = stepNotice;
    content.appendChild(stepNotice);

    var nav = document.createElement('div');
    nav.className = 'mw-nav';
    nav.innerHTML =
      '<button type="button" class="mw-nav__back">Back</button>' +
      '<button type="button" class="mw-nav__next mw-nav__next--continue">Continue \u2192</button>' +
      '<button type="button" class="mw-nav__express">Keep as-pictured</button>';
    wiz.navEl = nav;
    content.appendChild(nav);

    nav.querySelector('.mw-nav__back').addEventListener('click', function(e) {
      e.preventDefault(); goToStep(wiz.currentStep - 1);
    });
    nav.querySelector('.mw-nav__next').addEventListener('click', function(e) {
      e.preventDefault();
      if (wiz.currentStep < wiz.steps.length - 1) {
        goToStep(wiz.currentStep + 1);
      } else {
        wiz.reviewed = true;
        updateReviewCard();
        overlayDiscountedPrices();
        if (wiz.reviewCardEl) {
          wiz.reviewCardEl.style.display = '';
          wiz.reviewCardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });

    nav.querySelector('.mw-nav__express').addEventListener('click', function(e) {
      e.preventDefault();
      wiz.reviewed = true;
      updateReviewCard();
      overlayDiscountedPrices();
      if (wiz.reviewCardEl) {
        wiz.reviewCardEl.style.display = '';
        wiz.reviewCardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    addToggleTotal();
    reorderCapAndLace();
    addGraceHelpLinks();
    autoSelectCapSize();
    injectConsultCTA();
    enhanceFormElements();
    formatWizardPrices();
    watchToggleTitle();
    injectPriceContext();
    buildReviewCard();
    watchVariantChanges();
    autoBypassColorGate();
    enhanceProductPrice();

    updateToggleTotal();
    updatePriceContext();
    updateWizardTotal();
    fixSkinToSilk();

    setupColorExclusivity();
    autoSelectDefaults();
    autoSelectRecommended();

    var _changeDebounce = null;
    var _overlayRetry = null;
    content.addEventListener('change', function() {
      clearTimeout(_changeDebounce);
      clearTimeout(_overlayRetry);
      _changeDebounce = setTimeout(function() {
        updateCompletion();
        updateToggleTotal();
        updatePriceContext();
        updateWizardTotal();
        if (wiz.reviewed) updateReviewCard();

        applyLabelRenames();
        injectBadgesAndLabels();
        injectInlineHelp();
        updateStepNotice();
        injectAddonHeaders();
        formatWizardPrices();
        var overlayCount = overlayDiscountedPrices();

        // Retry only if we applied changes (SC Options may overwrite later).
        // If all-cached (0), no retry — saves the extra DOM pass.
        if (overlayCount > 0) {
          _overlayRetry = setTimeout(overlayDiscountedPrices, 350);
        }

        var nextBtn = wiz.navEl ? wiz.navEl.querySelector('.mw-nav__next') : null;
        if (nextBtn) {
          nextBtn.classList.remove('mw-pulse');
          void nextBtn.offsetWidth;
          nextBtn.classList.add('mw-pulse');
        }
      }, 200);
    });

    // Make entire option card clickable — not just the label/input area.
    // Delegated listener: one handler on the content element, catches clicks
    // on card padding/background that miss the label. Skips clicks on
    // interactive elements (links, buttons, help pills) to avoid conflicts.
    content.addEventListener('click', function(e) {
      var optVal = e.target.closest('.bold_option_value');
      if (!optVal) return;
      if (e.target.closest('label, input, a, button, .mw-inline-help')) return;
      var input = optVal.querySelector('input[type="radio"], input[type="checkbox"]');
      if (input) input.click();
    });

    wiz.active = true;
    goToStep(0);

    // Overlay discounted prices after SC Options has rendered prices.
    // Smart retry: second pass only if first pass applied changes (SC Options
    // might overwrite). If first pass was all-cached (0), no retry needed.
    setTimeout(function() {
      var n = overlayDiscountedPrices();
      if (n > 0) setTimeout(overlayDiscountedPrices, 500);
    }, 600);

    document.addEventListener('limoniapps:discountninja:product:discountapplied', function() {
      setTimeout(function() {
        updateToggleTotal();
        updatePriceContext();
        updateWizardTotal();
        if (wiz.reviewed) updateReviewCard();
        overlayDiscountedPrices();
      }, 200);
    });

    document.addEventListener('click', function(e) {
      if (!e.target) return;
      if (e.target.closest('.mw-lace-help, .mw-step-help, [data-popup]')) {
        document.body.dataset.scrollY = String(window.scrollY);
      }
      if (e.target.closest('.close-button') || e.target.classList.contains('close-button-img')) {
        var popup = e.target.closest('.pop-up-container');
        if (popup) {
          popup.style.display = 'none';
          document.body.classList.remove('mw-popup-open');
          document.body.style.overflow = '';
        }
      }
      if (e.target.classList && e.target.classList.contains('pop-up-container')) {
        e.target.style.display = 'none';
        document.body.classList.remove('mw-popup-open');
        document.body.style.overflow = '';
      }
    });

    console.log('[Milano Wizard] Transform complete \u2014 ' + wiz.steps.length + ' steps');
  }

  // =========================================================================
  // CLASSIFY
  // =========================================================================

  function classifyRow(name) {
    for (var s = 0; s < STEP_DEFS.length; s++)
      for (var p = 0; p < STEP_DEFS[s].patterns.length; p++)
        if (STEP_DEFS[s].patterns[p].test(name)) return STEP_DEFS[s].id;
    return STEP_DEFS[STEP_DEFS.length - 1].id;
  }

  // =========================================================================
  // GO TO STEP
  // =========================================================================

  function goToStep(stepIndex) {
    if (stepIndex < 0) stepIndex = 0;
    if (stepIndex >= wiz.steps.length) stepIndex = wiz.steps.length - 1;
    wiz.currentStep = stepIndex;

    for (var i = 0; i < wiz.steps.length; i++) {
      var isActive = i === stepIndex;
      for (var r = 0; r < wiz.steps[i].rows.length; r++) {
        var row = wiz.steps[i].rows[r];
        row.style.display = (!isActive || row.classList.contains('milano-option-row--hidden')) ? 'none' : '';
        row.classList.remove('mw-step-group-sep');
      }
    }

    var firstVisible = true;
    for (var g = 0; g < wiz.steps[stepIndex].rows.length; g++) {
      var gRow = wiz.steps[stepIndex].rows[g];
      if (gRow.style.display === 'none' || gRow.classList.contains('milano-option-row--hidden')) continue;
      if (!firstVisible) gRow.classList.add('mw-step-group-sep');
      firstVisible = false;
    }

    renderIndicator();

    if (wiz.hintEl) {
      var hintText = wiz.steps[stepIndex].hint || '';
      if (wiz.isTopper) hintText = hintText.replace(/your wig/gi, 'your topper').replace(/style it/gi, 'style it');
      wiz.hintEl.textContent = hintText;
    }
    if (wiz.subhintEl) {
      var sh = wiz.steps[stepIndex].subhint || '';
      if (wiz.isTopper) sh = sh.replace(/your wig/gi, 'your topper').replace(/each wig/gi, 'each topper');
      wiz.subhintEl.textContent = sh;
      wiz.subhintEl.style.display = sh ? '' : 'none';
    }

    if (wiz.stepHelpEl) {
      var stepDef = wiz.steps[stepIndex];
      var helpId = resolveStepHelpId(stepDef);
      var helpText = stepDef.helpText || 'Learn more';

      if (helpId) {
        wiz.stepHelpEl.setAttribute('data-popup-id', helpId);
        wiz.stepHelpEl.innerHTML =
          '<svg class="mw-step-help__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
            '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
            '<text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="10" font-weight="700" font-family="inherit">?</text>' +
          '</svg>' +
          '<span>' + helpText + '</span>';
        wiz.stepHelpEl.style.display = '';
      } else {
        wiz.stepHelpEl.style.display = 'none';
      }
    }

    var backBtn = wiz.navEl.querySelector('.mw-nav__back');
    var nextBtn = wiz.navEl.querySelector('.mw-nav__next');
    var expressBtn = wiz.navEl.querySelector('.mw-nav__express');
    backBtn.style.display = stepIndex > 0 ? '' : 'none';

    if (stepIndex === wiz.steps.length - 1) {
      nextBtn.textContent = '\u2713 Review My ' + (wiz.isTopper ? 'Topper' : 'Wig');
      nextBtn.className = 'mw-nav__next mw-nav__next--review';
      if (expressBtn) expressBtn.style.display = 'none';
    } else {
      nextBtn.textContent = 'Continue \u2192';
      nextBtn.className = 'mw-nav__next mw-nav__next--continue';
      if (expressBtn) expressBtn.style.display = '';
    }

    updateCompletion();
    applyLabelRenames();
    updateStepNotice();
    var panelEl = document.querySelector('.milano-panel');
    if (panelEl) panelEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // =========================================================================
  // INDICATOR
  // =========================================================================

  function renderIndicator() {
    if (!wiz.indicatorEl) return;
    var html = '';
    for (var i = 0; i < wiz.steps.length; i++) {
      var step = wiz.steps[i];
      var isActive = i === wiz.currentStep;
      var isDone = step.done && !isActive;
      var cls = 'mw-pill';
      if (isActive) cls += ' mw-pill--active';
      if (isDone) cls += ' mw-pill--done';

      html += '<button type="button" class="' + cls + '" data-step="' + i + '">';
      if (isDone) {
        html += '<svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round"/></svg>';
      }
      html += ' <span class="mw-pill__label">' + step.label + '</span></button>';
      if (i < wiz.steps.length - 1) html += '<span class="mw-pill__sep"></span>';
    }
    wiz.indicatorEl.innerHTML = html;
  }

  // =========================================================================
  // COMPLETION
  // =========================================================================

  function updateCompletion() {
    for (var i = 0; i < wiz.steps.length; i++) {
      var has = false;
      for (var r = 0; r < wiz.steps[i].rows.length; r++) {
        var row = wiz.steps[i].rows[r];
        if (!row.classList.contains('milano-option-row--hidden') && row.classList.contains('milano-option-row--selected')) { has = true; break; }
      }
      wiz.steps[i].done = has;
    }
    renderIndicator();
  }

  // =========================================================================
  // RENAME TOGGLE — "Customize" → "Personalize" on ALL products
  //
  // Runs before the wizard step check so even single-step / STL products
  // get the correct copy. Wizard-mode products get further updates in
  // watchToggleTitle() (open/closed state copy).
  // =========================================================================

  function renameToggleTitle() {
    var titleEl = document.querySelector('.personalize-toggle__title');
    if (!titleEl) return;
    var subtitleEl = document.querySelector('.personalize-toggle__subtitle');
    var prodType = wiz.isTopper ? 'Topper' : 'Wig';
    titleEl.textContent = 'Personalize This ' + prodType;

    if (!subtitleEl) return;

    // Build subtitle from actual options present (flat mode only —
    // wizard mode overwrites this in watchToggleTitle())
    var parts = [];
    var hasSecure = false, hasEar = false, hasHairline = false;
    var rows = document.querySelectorAll('.milano-option-row');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].classList.contains('milano-option-row--hidden')) continue;
      var name = (rows[i].getAttribute('data-option-name') || '').toLowerCase();
      if (name.indexOf('secure') > -1 && !hasSecure) { parts.push('secure method'); hasSecure = true; }
      if (name.indexOf('ear') > -1 && !hasEar) { parts.push('ear pieces'); hasEar = true; }
      if ((name.indexOf('hairline') > -1 || name.indexOf('blending') > -1) && !hasHairline) { parts.push('hairline'); hasHairline = true; }
    }

    if (parts.length === 0) {
      subtitleEl.textContent = 'Choose your options';
    } else if (parts.length === 1) {
      subtitleEl.textContent = 'Choose your ' + parts[0];
    } else {
      var last = parts.pop();
      subtitleEl.textContent = parts.join(', ') + ' & ' + last;
    }
  }

  // =========================================================================
  // FLAT OPTIONS MODE — Simple products (STL, 1-step sets)
  //
  // Shows accordion row contents directly without headers or collapse/expand.
  // The toggle still works (open/close panel) but inside it's flat options.
  // Adds body class for CSS to remove accordion borders and spacing.
  // =========================================================================

  function flattenOptions(content, rows) {
    document.body.classList.add('milano-flat-options');

    // Inject flat mode styles (small, self-contained — no wizard CSS active)
    if (!document.getElementById('mw-flat-styles')) {
      var style = document.createElement('style');
      style.id = 'mw-flat-styles';
      style.textContent =
        'body.milano-flat-options .milano-option-row { border: none; padding: 0; margin: 0 0 4px; }' +
        'body.milano-flat-options .milano-option-row:first-child { border-top: none; margin-top: 0; }' +
        'body.milano-flat-options .milano-option-row__body { max-height: none !important; overflow: visible !important; }' +
        'body.milano-flat-options .milano-option-row__header { display: none !important; }' +
        'body.milano-flat-options .milano-option-row__body .bold_option { padding-bottom: 6px; }' +
        'body.milano-flat-options .milano-summary { display: none !important; }';
      document.head.appendChild(style);
    }

    // Show all row bodies, hide accordion headers
    for (var i = 0; i < rows.length; i++) {
      var header = rows[i].querySelector('.milano-option-row__header');
      if (header) header.style.display = 'none';

      var body = rows[i].querySelector('.milano-option-row__body');
      if (body) {
        body.style.maxHeight = 'none';
        body.style.overflow = 'visible';
        body.style.display = 'block';
      }

      // Show the row (accordion may have hidden conditional rows)
      if (!rows[i].classList.contains('milano-option-row--hidden')) {
        rows[i].style.display = '';
      }
    }

    console.log('[Milano Wizard] Flat options: ' + rows.length + ' row(s) visible');
  }

  // =========================================================================
  // TOGGLE — Services total (+$X) next to title
  // =========================================================================

  function addToggleTotal() {
    var titleEl = document.querySelector('.personalize-toggle__title');
    if (!titleEl) return;
    var totalSpan = document.createElement('span');
    totalSpan.className = 'mw-toggle-total';
    titleEl.parentNode.insertBefore(totalSpan, titleEl.nextSibling);
    wiz.toggleTotalEl = totalSpan;
  }

  function getActualServicesDollars() {
    var dollars = getServicesDollars();
    if (dollars <= 0) return 0;
 
    // Read DN discount ratio from sessionStorage (set on collection pages
    // where DN is active). Proportionally reduces the displayed services
    // amount during sales (e.g., 25% off → $775 services → $581).
    var dnRatio = 0;
    try {
      var stored = sessionStorage.getItem('mw_dn_ratio');
      if (stored) dnRatio = parseFloat(stored) || 0;
    } catch (e) {}
 
    if (dnRatio > 0 && dnRatio < 1) {
      return Math.round(dollars * (1 - dnRatio));
    }
    return dollars;
  }
 

  function updateToggleTotal() {
    if (!wiz.toggleTotalEl) return;
    var dollars = getActualServicesDollars();
    var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
    var panelOpen = checkbox && checkbox.checked;
    if (dollars > 0 && !panelOpen) {
      wiz.toggleTotalEl.textContent = '+$' + dollars;
      wiz.toggleTotalEl.style.display = 'inline-block';
    } else {
      wiz.toggleTotalEl.style.display = 'none';
    }
  }

  function updateWizardTotal() {
    if (!wiz.wizTotalEl) return;
    var dollars = getActualServicesDollars();
    if (dollars > 0) {
      wiz.wizTotalEl.textContent = '+$' + dollars;
      wiz.wizTotalEl.style.display = 'inline-block';
    } else {
      wiz.wizTotalEl.style.display = 'none';
    }
  }

  function watchToggleTitle() {
    var titleEl = document.querySelector('.personalize-toggle__title');
    var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
    if (!titleEl || !checkbox) return;
    var subtitleEl = document.querySelector('.personalize-toggle__subtitle');

    function update() {
      var prodType = wiz.isTopper ? 'Topper' : 'Wig';
      titleEl.textContent = checkbox.checked ? 'Personalizing Your ' + prodType : 'Personalize This ' + prodType;
      if (subtitleEl) subtitleEl.textContent = checkbox.checked
        ? 'All optional \u00B7 Tap any step to jump'
        : 'Color, cut & styling \u2014 or keep as-is';
      updateToggleTotal();
    }
    update();
    checkbox.addEventListener('change', update);
  }

  // =========================================================================
  // AUTO-SELECT CAP SIZE
  // =========================================================================

  function autoSelectCapSize() {
    var capEl = null;
    var allVisible = document.querySelectorAll('.milano-always-visible');
    for (var i = 0; i < allVisible.length; i++) {
      var t = allVisible[i].querySelector('.bold_option_title');
      var name = t ? (t.textContent || '').trim().toLowerCase() : '';
      if (name.indexOf('cap size') > -1) { capEl = allVisible[i]; break; }
    }
    if (!capEl) return;

    var collectionKey = detectCollection();
    var target = 'medium';
    var capLabel = target.charAt(0).toUpperCase() + target.slice(1);
    var helperText = 'Most wear ' + capLabel + '. Not sure? Size guide takes 2 min.';

    if (collectionKey && CAP_SIZE_MAP[collectionKey]) {
      target = CAP_SIZE_MAP[collectionKey].size;
      capLabel = target.charAt(0).toUpperCase() + target.slice(1);
      helperText = 'Most wear ' + capLabel + '. Not sure? Size guide takes 2 min.';
    }

    var radios = capEl.querySelectorAll('input[type="radio"]');
    for (var r = 0; r < radios.length; r++) {
      var label = radios[r].parentElement.querySelector('label') || radios[r].nextElementSibling;
      if (!label) continue;
      var labelText = (label.textContent || '').trim().toLowerCase();
      if (labelText.indexOf(target) > -1 && !radios[r].checked) {
        radios[r].checked = true;
        radios[r].dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }

    var helper = capEl.querySelector('.mw-cap-helper');
    if (helper) {
      helper.textContent = helperText;
    }
  }

  // =========================================================================
  // FORMAT WIZARD PRICES
  // =========================================================================

  function formatWizardPrices() {
    var prices = document.querySelectorAll('.milano-option-row__body .bold_option_value_price');
    for (var p = 0; p < prices.length; p++) {
      var el = prices[p];
      if (el.querySelector('.mw-included')) continue;
      if (el.hasAttribute('data-mw-discounted')) continue; // skip sale-overlaid prices
      var txt = (el.textContent || '').trim();
      el.textContent = txt.replace(/\.00/g, '');
    }

    var previews = document.querySelectorAll('.milano-option-row__preview');
    for (var v = 0; v < previews.length; v++) {
      previews[v].textContent = (previews[v].textContent || '').replace(/\.00/g, '');
    }
  }

  // =========================================================================
  // COLOR EXCLUSIVITY
  // =========================================================================

  function setupColorExclusivity() {
    var noColorEl = document.querySelector('.mw-no-color');
    if (!noColorEl) return;
    var noColorInput = noColorEl.querySelector('input[type="checkbox"]');
    if (!noColorInput) return;

    var parent = noColorEl.parentElement;
    if (!parent) return;

    function getPaidBoxes() {
      var boxes = [];
      var vals = parent.querySelectorAll('.bold_option_value:not(.mw-no-color) input[type="checkbox"]');
      for (var i = 0; i < vals.length; i++) boxes.push(vals[i]);
      return boxes;
    }

    noColorInput.addEventListener('change', function() {
      if (noColorInput.checked) {
        var paid = getPaidBoxes();
        for (var i = 0; i < paid.length; i++) {
          if (paid[i].checked) { paid[i].checked = false; paid[i].dispatchEvent(new Event('change', { bubbles: true })); }
        }
      }
    });

    parent.addEventListener('change', function(e) {
      if (e.target === noColorInput) return;
      if (e.target.type === 'checkbox' && e.target.checked && noColorInput.checked) {
        noColorInput.checked = false;
        noColorInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // =========================================================================
  // AUTO-BYPASS COLOR SERVICE GATE
  // =========================================================================

  function autoBypassColorGate() {
    var allOptions = document.querySelectorAll('.milano-option-row .bold_option');
    for (var i = 0; i < allOptions.length; i++) {
      var name = (allOptions[i].getAttribute('data-option-name') || '').toLowerCase();
      if (!name) {
        var titleEl = allOptions[i].querySelector('.bold_option_title');
        name = titleEl ? (titleEl.textContent || '').trim().toLowerCase() : '';
      }
      if (name.indexOf('add color') > -1 || name.indexOf('color service') > -1) {
        var select = allOptions[i].querySelector('select');
        if (select) {
          for (var o = 0; o < select.options.length; o++) {
            if (select.options[o].value.toLowerCase() === 'yes' || select.options[o].text.toLowerCase() === 'yes') {
              select.selectedIndex = o;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              var gateRow = allOptions[i].closest('.milano-option-row');
              if (gateRow) gateRow.style.display = 'none';
              console.log('[Milano Wizard] Color gate auto-bypassed');
              break;
            }
          }
        }
      }
    }
  }

  // =========================================================================
  // AUTO-SELECT DEFAULTS
  // =========================================================================

  function autoSelectDefaults() {
    var allValues = document.querySelectorAll('.milano-option-row__body .bold_option_value');
    var processedRows = [];
    var selected = 0;

    for (var i = 0; i < allValues.length; i++) {
      var lbl = allValues[i].querySelector('label');
      if (!lbl) continue;
      var text = stripPriceText(lbl.textContent);

      var isSkip = (text.indexOf('as is') > -1 ||
                    text.indexOf('as-pictured') > -1 ||
                    text.indexOf('no cut') > -1 ||
                    text.indexOf('no color') > -1 ||
                    text.indexOf('keep as pictured') > -1 ||
                    text.indexOf('keep original') > -1);
      if (!isSkip) continue;

      var optionRow = allValues[i].closest('.milano-option-row');
      if (!optionRow) continue;
      var rowId = optionRow.getAttribute('data-option-name') || '';
      if (processedRows.indexOf(rowId) > -1) continue;
      processedRows.push(rowId);

      var body = optionRow.querySelector('.milano-option-row__body');
      if (!body) continue;
      if (body.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) continue;
      var hasSelectedDropdown = false;
      var selects = body.querySelectorAll('select');
      for (var s = 0; s < selects.length; s++) {
        if (selects[s].selectedIndex > 0) { hasSelectedDropdown = true; break; }
      }
      if (hasSelectedDropdown) continue;

      var input = allValues[i].querySelector('input[type="radio"], input[type="checkbox"]');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        selected++;
        console.log('[Milano Wizard] Auto-selected: "' + text + '" in "' + rowId + '"');
      }
    }

    if (selected > 0) {
      console.log('[Milano Wizard] Auto-selected ' + selected + ' default(s)');
    }
  }

  // =========================================================================
  // AUTO-SELECT RECOMMENDED
  // =========================================================================

  function autoSelectRecommended() {
    var badges = document.querySelectorAll('.milano-option-row__body .mw-badge');
    var selected = 0;

    for (var b = 0; b < badges.length; b++) {
      if (badges[b].textContent.trim() !== 'Recommended') continue;

      var optVal = badges[b].closest('.bold_option_value');
      if (!optVal) continue;

      var optRow = optVal.closest('.milano-option-row');
      if (!optRow) continue;

      var body = optRow.querySelector('.milano-option-row__body');
      if (!body) continue;
      if (body.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) continue;

      var priceEl = optVal.querySelector('.bold_option_value_price');
      var priceText = priceEl ? (priceEl.textContent || '').replace(/[^\d.]/g, '') : '';
      var price = parseFloat(priceText) || 0;
      if (price > 0) {
        console.log('[Milano Wizard] Skipped priced recommended: "' + (optRow.getAttribute('data-option-name') || '') + '" ($' + price + ')');
        continue;
      }

      var input = optVal.querySelector('input[type="radio"], input[type="checkbox"]');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        selected++;
        console.log('[Milano Wizard] Auto-selected recommended: "' + (optRow.getAttribute('data-option-name') || '') + '"');
      }
    }

    var allRows = document.querySelectorAll('.milano-option-row');
    for (var r = 0; r < allRows.length; r++) {
      var fbName = (allRows[r].getAttribute('data-option-name') || '').toLowerCase();
      if (fbName.indexOf('secure') === -1) continue;

      var fbBody = allRows[r].querySelector('.milano-option-row__body');
      if (!fbBody) continue;
      if (fbBody.querySelector('input:checked')) continue;
      if (!fbBody.querySelector('input[type="radio"]')) continue;

      var fbVals = fbBody.querySelectorAll('.bold_option_value');
      for (var v = 0; v < fbVals.length; v++) {
        var fbPrice = fbVals[v].querySelector('.bold_option_value_price');
        var fbText = fbPrice ? (fbPrice.textContent || '').replace(/[^\d.]/g, '') : '';
        var fbAmt = parseFloat(fbText) || 0;
        if (fbAmt > 0) continue;
        var fbInput = fbVals[v].querySelector('input[type="radio"]');
        if (fbInput && !fbInput.checked) {
          fbInput.checked = true;
          fbInput.dispatchEvent(new Event('change', { bubbles: true }));
          selected++;
          console.log('[Milano Wizard] Auto-selected first free option: "' + (allRows[r].getAttribute('data-option-name') || '') + '"');
          break;
        }
      }
    }

    if (selected > 0) {
      console.log('[Milano Wizard] Auto-selected ' + selected + ' recommended/default option(s)');
    }
  }

  // =========================================================================
  // PRODUCT PRICE
  // =========================================================================

  function enhanceProductPrice() {
    var priceEl = document.querySelector('.product__price .money, [data-product-price] .money, .product__price-and-badge .money');
    if (!priceEl) priceEl = document.querySelector('[data-product-price]');
    if (priceEl && !priceEl.classList.contains('mw-price-enhanced')) {
      priceEl.classList.add('mw-price-enhanced');
    }
  }

  // =========================================================================
  // PRICE CONTEXT
  // =========================================================================

  function injectPriceContext() {
    if (document.querySelector('.mw-price-context')) return;
    var contextEl = document.createElement('span');
    contextEl.className = 'mw-price-context';
    contextEl.style.display = 'none';

    var priceWrapper = document.querySelector('[data-price-wrapper]:not([data-atc-button])');
    if (!priceWrapper) priceWrapper = document.querySelector('.product__price-and-badge');
    if (!priceWrapper) return;

    // Insert right after the main price element so it stays on the same
    // flex row as the price. Appending to the container put it after
    // Splitit/ShopPay (which get flex-basis:100% when services active),
    // pushing it to its own line.
    var priceEl = priceWrapper.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.insertAdjacentElement('afterend', contextEl);
    } else {
      priceWrapper.insertBefore(contextEl, priceWrapper.children[1] || null);
    }

    var priceParent = priceWrapper.parentNode;
    var priceObs = new MutationObserver(function() {
      if (!document.querySelector('.mw-price-context')) {
        var newCtx = document.createElement('span');
        newCtx.className = 'mw-price-context';
        var pw = document.querySelector('[data-price-wrapper]:not([data-atc-button])') ||
                 document.querySelector('.product__price-and-badge');
        if (pw) {
          var pe = pw.querySelector('[data-product-price]');
          if (pe) pe.insertAdjacentElement('afterend', newCtx);
          else pw.insertBefore(newCtx, pw.children[1] || null);
        }
        updatePriceContext();
      }
    });
    priceObs.observe(priceParent, { childList: true, subtree: true });
    wiz.observers.push(priceObs);
  }

  function updatePriceContext() {
    var contextEl = document.querySelector('.mw-price-context');
    if (!contextEl) return;
    var dollars = getActualServicesDollars();

    var priceWrap = document.querySelector('.product__price-and-badge');
    if (priceWrap) priceWrap.classList.toggle('mw-has-services', dollars > 0);

    if (dollars > 0) {
      contextEl.textContent = '(+$' + dollars + ' services)';
      contextEl.style.display = '';
    } else {
      contextEl.style.display = 'none';
    }
  }

  // =========================================================================
  // REVIEW CARD
  // =========================================================================

  function buildReviewCard() {
    var card = document.createElement('div');
    card.className = 'mw-review-card';
    card.style.display = 'none';

    var panel = document.querySelector('.milano-panel');
    if (panel) panel.parentNode.insertBefore(card, panel.nextSibling);
    wiz.reviewCardEl = card;
  }

  function updateReviewCard() {
    if (!wiz.reviewCardEl) return;

    var items = document.querySelectorAll('.milano-summary__item');
    if (items.length === 0) return;

    var capValue = '', laceValue = '';
    var capRadio = document.querySelector('.milano-always-visible input[type="radio"]:checked');
    if (capRadio) {
      var capLbl = capRadio.parentElement.querySelector('label') || capRadio.nextElementSibling;
      capValue = capLbl ? capLbl.textContent.trim().replace(/\$[\d,.]+/g, '').trim() : '';
    }
    if (!capValue) {
      var capSelect = document.querySelector('.milano-always-visible select');
      if (capSelect && capSelect.selectedIndex > 0) capValue = capSelect.options[capSelect.selectedIndex].text.trim();
    }
    var laceRadio = document.querySelector('.radio__buttons input[type="radio"]:checked');
    if (laceRadio) {
      var laceLbl = laceRadio.parentElement.querySelector('.option-title');
      laceValue = laceLbl ? laceLbl.textContent.trim() : '';
    }

    var html = '<div class="mw-review-card__title">YOUR CUSTOM WIG SUMMARY</div>';
    html += '<div class="mw-review-card__items">';

    if (capValue || laceValue) {
      html += '<div class="mw-review-card__line">';
      if (capValue) html += 'Cap: <strong>' + capValue + '</strong>';
      if (capValue && laceValue) html += ' \u00B7 ';
      if (laceValue) html += 'Lace: <strong>' + laceValue + '</strong>';
      html += '</div>';
    }

    for (var i = 0; i < items.length; i++) {
      var nameEl = items[i].querySelector('.milano-summary__item-name');
      var priceEl = items[i].querySelector('.milano-summary__item-price');
      if (!nameEl) continue;

      var rawName = nameEl.textContent.trim();
      var price = priceEl ? priceEl.textContent.trim() : '';

      var cleanedName = rawName.replace(/\s*[\(\+]?\$[\d,.]+\s*(usd)?\)?/gi, '').trim();

      var short = cleanedName
        .replace(/^.*Secure.*Method:\s*/i, 'Secure: ')
        .replace(/^Add Color Service:\s*/i, 'Color: ')
        .replace(/^Color Service[s]?.*:\s*/i, 'Color: ')
        .replace(/^Topper Color.*:\s*/i, 'Color: ')
        .replace(/^Personalize Your Cut:\s*/i, 'Cut: ')
        .replace(/^Milano Signature.*Cut:\s*/i, 'Cut: ')
        .replace(/^Please Style My (?:Wig|Ponytail|Topper|BandFall):\s*/i, 'Style: ')
        .replace(/^Style My (?:Wig|Ponytail|Topper|BandFall):\s*/i, 'Style: ')
        .replace(/^Location of Part:\s*/i, 'Part: ')
        .replace(/^Position of Face Framing:\s*/i, 'Framing: ')
        .replace(/^Bangs or Fringe:\s*/i, 'Bangs: ')
        .replace(/^Adjust (?:Ponytail )?Cap Size:\s*/i, 'Cap: ')
        .replace(/^Add Ear Pieces:\s*/i, 'Ear Pieces: ')
        .replace(/^Are You Blending.*:\s*/i, 'Hairline: ')
        .replace(/^Front Hairline:\s*/i, 'Hairline: ')
        .replace(/Most chosen/gi, '')
        .replace(/Recommended/gi, '')
        .replace(/Included/gi, '')
        .replace(/\s*\u2014\s.+$/g, '')
        .trim();

      var colonIdx = short.indexOf(': ');
      if (colonIdx > -1) {
        var valPart = short.substring(colonIdx + 2).trim();
        short = short.substring(0, colonIdx + 2) + '<strong>' + valPart + '</strong>';
      }

      var cleanPrice = price.replace(/\.00/g, '').replace(/\s*(usd)/gi, '').trim();
      if (cleanPrice === '+$0' || cleanPrice === '$0' || cleanPrice === '') cleanPrice = '';

      html += '<div class="mw-review-card__line">';
      html += short;
      if (cleanPrice) html += ' <span class="mw-review-card__price">(' + cleanPrice + ')</span>';
      html += '</div>';
    }

    html += '</div>';

    var fw = document.querySelector('product-form[data-form-wrapper]');
    var serviceDollars = getServicesDollars();
    var origBaseCents = fw ? parseInt(fw.getAttribute('data-variant-price') || '0', 10) : 0;
    var origBase = origBaseCents > 0 ? origBaseCents / 100 : 0;

    // Read DN discount ratio from sessionStorage
    var dnRatio = 0;
    try {
      var stored = sessionStorage.getItem('mw_dn_ratio');
      if (stored) dnRatio = parseFloat(stored) || 0;
    } catch (e) {}

    // Use SC Options element as single source of truth for total price.
    // This matches computeFinalPrice() in personalize-options.js, ensuring
    // the review card total equals the cart bar total. SC Options includes
    // conditional sub-options (Face Framing, Bangs) that getServicesDollars()
    // might miss if they were added after the initial calculation.
    var scEl = document.querySelector('[data-product-price].bold_option_price_display');
    var scTotal = 0;
    if (scEl) {
      scTotal = parseFloat((scEl.textContent || '').replace(/[^\d.]/g, '')) || 0;
    }
    var fullPrice = scTotal > 0 ? scTotal : (origBase + serviceDollars);
    // Recalculate serviceDollars from SC Options total for accurate breakdown
    if (scTotal > 0 && origBase > 0 && scTotal > origBase) {
      serviceDollars = Math.round(scTotal - origBase);
    }

    var currentTotal = fullPrice;
    if (dnRatio > 0 && dnRatio < 1) {
      currentTotal = Math.round(fullPrice * (1 - dnRatio));
    }

    var isDiscounted = dnRatio > 0 && dnRatio < 1 && fullPrice > 0;
 

    var displayBase = origBase;
    var displayServices = serviceDollars;

    if (isDiscounted && fullPrice > 0) {
      var ratio = currentTotal / fullPrice;
      displayBase = Math.round(origBase * ratio);
      displayServices = Math.round(serviceDollars * ratio);
    }

    html += '<div class="mw-review-card__total">';
    if (origBase > 0) {
      if (isDiscounted) {
        var discountPct = Math.round((1 - (currentTotal / fullPrice)) * 100);
        var savedAmount = Math.round(fullPrice - currentTotal);

        html += '<div class="mw-review-card__line">Wig: <s class="mw-review-card__strike">$' + origBase.toFixed(0) + '</s> <strong>$' + displayBase + '</strong></div>';
        if (serviceDollars > 0) {
          html += '<div class="mw-review-card__line">Services: <s class="mw-review-card__strike">$' + serviceDollars + '</s> <strong>+$' + displayServices + '</strong></div>';
        }

        // "You save" — minimal tinted line with tag icon
        html += '<div class="mw-review-card__savings">';
        html += '<span class="mw-review-card__savings-icon" aria-hidden="true">';
        html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';
        html += '</span>';
        html += '<span>You save <strong>$' + savedAmount + '</strong></span>';
        html += '</div>';

      } else {
        html += '<div class="mw-review-card__line">Wig: <strong>$' + origBase.toFixed(0) + '</strong></div>';
        if (displayServices > 0) {
          html += '<div class="mw-review-card__line">Services: <strong>+$' + displayServices + '</strong></div>';
        }
      }
      html += '<div class="mw-review-card__total-line">Total: <strong>$' + currentTotal.toFixed(0) + '</strong></div>';
    } else if (serviceDollars > 0) {
      html += 'Services: <strong>+$' + serviceDollars + '</strong>';
    }
    html += '</div>';

    // Review card reminder — show when color or cut services are selected
    var reminderParts = [];
    for (var si = 0; si < wiz.steps.length; si++) {
      var stepCfg = STEP_REASSURANCE[wiz.steps[si].id];
      if (!stepCfg) continue;
      var stepHasPaid = false;
      for (var sr = 0; sr < wiz.steps[si].rows.length; sr++) {
        var sRow = wiz.steps[si].rows[sr];
        if (sRow.classList.contains('milano-option-row--hidden')) continue;
        var sChecked = sRow.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
        for (var sc = 0; sc < sChecked.length; sc++) {
          var sVal = sChecked[sc].closest('.bold_option_value');
          if (!sVal || sVal.classList.contains('mw-no-color') || sVal.classList.contains('mw-no-cut') || sVal.classList.contains('mw-no-style')) continue;
          var sPriceEl = sVal.querySelector('.bold_option_value_price');
          var sPriceText = sPriceEl ? (sPriceEl.textContent || '').replace(/[^\d.]/g, '') : '';
          if ((parseFloat(sPriceText) || 0) > 0) { stepHasPaid = true; break; }
        }
        if (stepHasPaid) break;
      }
      if (stepHasPaid) reminderParts.push(stepCfg.reviewLabel);
    }

    if (reminderParts.length > 0) {
      var reminderText = reminderParts.join(' & ');
      html += '<div class="mw-review-card__reminder">';
      html += '\u2709\uFE0E Our team will email you about your ' + reminderText + ' after your order.';
      html += '</div>';
    }

    html += '<div class="mw-review-card__actions">';
    html += '<a href="#" class="mw-review-card__edit">Edit selections</a>';
    html += '</div>';

    wiz.reviewCardEl.innerHTML = html;

    var editLink = wiz.reviewCardEl.querySelector('.mw-review-card__edit');
    if (editLink) {
      editLink.addEventListener('click', function(e) {
        e.preventDefault();
        goToStep(0);
      });
    }
  }

  // =========================================================================
  // WIZARD OPTION PRICE DISCOUNT OVERLAY (v1.5.4-sale)
  //
  // SC Options renders full (undiscounted) prices on each option card.
  // When DN ratio is active, this overlays the discounted price next to
  // a subtle strikethrough of the original — e.g., "~~$165~~ $132".
  //
  // Idempotent: [data-mw-discounted] cache key prevents double-processing.
  // Reversible: stores original text for clean revert when sale ends.
  // =========================================================================

  function overlayDiscountedPrices() {
    var dnRatio = 0;
    try {
      var stored = sessionStorage.getItem('mw_dn_ratio');
      if (stored) dnRatio = parseFloat(stored) || 0;
    } catch (e) {}

    var priceEls = document.querySelectorAll(
      'body.milano-wizard-mode .bold_option_value_price'
    );

    var applied = 0;
    for (var i = 0; i < priceEls.length; i++) {
      var el = priceEls[i];

      // No sale — revert any previously overlaid prices
      if (!(dnRatio > 0 && dnRatio < 1)) {
        if (el.hasAttribute('data-mw-discounted')) {
          el.textContent = el.getAttribute('data-mw-orig-text');
          el.removeAttribute('data-mw-discounted');
          el.removeAttribute('data-mw-orig-text');
        }
        continue;
      }

      // Detect SC Options overwrite: attribute survives but children are gone.
      // If data-mw-discounted is set but <ins> is missing, SC Options rewrote
      // the textContent after our overlay. Clear cache so we re-apply.
      if (el.hasAttribute('data-mw-discounted') && !el.querySelector('ins')) {
        el.removeAttribute('data-mw-discounted');
      }

      // Read the original dollar amount (from cache if already processed)
      var rawText = el.hasAttribute('data-mw-orig-text')
        ? el.getAttribute('data-mw-orig-text')
        : el.textContent.trim();

      var match = rawText.match(/\$?([\d,]+(?:\.\d{2})?)/);
      if (!match) continue;

      var origDollars = parseFloat(match[1].replace(/,/g, ''));
      if (origDollars <= 0) continue;

      var discounted = Math.round(origDollars * (1 - dnRatio));

      // Cache key prevents redundant DOM writes
      var cacheKey = origDollars + ':' + dnRatio.toFixed(6);
      if (el.getAttribute('data-mw-discounted') === cacheKey) continue;

      // Store original for clean revert
      if (!el.hasAttribute('data-mw-orig-text')) {
        el.setAttribute('data-mw-orig-text', rawText);
      }

      // Preserve the prefix format (+$, $, +, etc.)
      var prefix = '';
      var prefixMatch = rawText.match(/^([+\s]*)/);
      if (prefixMatch) prefix = prefixMatch[1];
      var dollarSign = rawText.indexOf('$') > -1 ? '$' : '';

      el.innerHTML =
        '<del class="mw-opt-price-orig">' + prefix + dollarSign +
          origDollars.toFixed(0) + '</del> ' +
        '<ins class="mw-opt-price-sale">' + prefix + dollarSign +
          discounted + '</ins>';

      el.setAttribute('data-mw-discounted', cacheKey);
      applied++;
    }
    return applied; // 0 = all cached (free), >0 = wrote DOM
  }

  // =========================================================================
  // ENHANCE FORM
  // =========================================================================

  function enhanceFormElements() {
    injectLaceSubtitles();
    applyLabelRenames();
    injectBadgesAndLabels();
    injectInlineHelp();
    injectAddonHeaders();
    reorderColorOptions();
  }

  // =========================================================================
  // LACE SUBTITLES
  // =========================================================================

  function injectLaceSubtitles() {
    var radioButtons = document.querySelectorAll('.radio__buttons .radio__button');
    for (var rb = 0; rb < radioButtons.length; rb++) {
      var labelEl = radioButtons[rb].querySelector('label.radio__label');
      if (!labelEl) continue;
      var titleSpan = labelEl.querySelector('.option-title');
      var text = titleSpan ? titleSpan.textContent.trim().toLowerCase() : labelEl.textContent.trim().toLowerCase();
      var sub = LACE_SUBS[text];
      if (sub && !labelEl.querySelector('.mw-lace-sub')) {
        var el = document.createElement('span');
        el.className = 'mw-lace-sub';
        el.textContent = sub;
        labelEl.appendChild(el);
      }
    }
  }

  // =========================================================================
  // LABEL RENAMES
  // =========================================================================

  function applyLabelRenames() {
    var vals = document.querySelectorAll('.milano-option-row__body .bold_option_value');
    for (var i = 0; i < vals.length; i++) {
      var lbl = vals[i].querySelector('label');
      if (!lbl || lbl.getAttribute('data-mw-renamed')) continue;
      var key = stripPriceText(lbl.textContent);
      if (!LABEL_RENAMES[key]) continue;

      var renamed = false;

      for (var n = 0; n < lbl.childNodes.length; n++) {
        if (lbl.childNodes[n].nodeType === 3) {
          var orig = lbl.childNodes[n].textContent;
          if (orig.trim().toLowerCase().indexOf(key) > -1) {
            lbl.childNodes[n].textContent = orig.replace(
              new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'),
              LABEL_RENAMES[key]
            );
            renamed = true;
            break;
          }
        }
      }

      if (!renamed) {
        var spans = lbl.querySelectorAll('span:not(.bold_option_value_price):not(.mw-badge):not(.mw-no-color-sub):not(.mw-included)');
        for (var s = 0; s < spans.length; s++) {
          if (spans[s].textContent.trim().toLowerCase() === key) {
            spans[s].textContent = LABEL_RENAMES[key];
            renamed = true;
            break;
          }
        }
      }

      if (renamed) lbl.setAttribute('data-mw-renamed', '1');
    }
  }

  // =========================================================================
  // BADGES AND LABELS
  // =========================================================================

  function injectBadgesAndLabels() {
    var vals = document.querySelectorAll('.milano-option-row__body .bold_option_value');
    for (var i = 0; i < vals.length; i++) {
      var lbl = vals[i].querySelector('label');
      if (!lbl) continue;
      var text = stripPriceText(lbl.textContent);

      if (text.indexOf('clips') > -1 &&
          (text.indexOf('wigrip') > -1 || text.indexOf('wi grip') > -1 || text.indexOf('wig grip') > -1 ||
           text.indexOf('topgrip') > -1 || text.indexOf('top grip') > -1 || text.indexOf('top-grip') > -1) &&
          !vals[i].querySelector('.mw-badge')) {
        var badge = document.createElement('span');
        badge.className = 'mw-badge';
        badge.textContent = 'Recommended';
        lbl.appendChild(badge);
      }

      if (text.indexOf('no color service') > -1 || text.indexOf('keep original color') > -1 || (text.indexOf('no color') > -1 && text.indexOf('keep as pictured') > -1)) {
        vals[i].classList.add('mw-no-color');
        if (!vals[i].nextElementSibling || !vals[i].nextElementSibling.classList.contains('mw-color-sep')) {
          var sep = document.createElement('div');
          sep.className = 'mw-color-sep';
          sep.textContent = 'Select one or more \u2014 you can combine services:';
          vals[i].parentNode.insertBefore(sep, vals[i].nextSibling);
        }
      }

      if ((text.indexOf('as is') > -1 || text.indexOf('as-pictured') > -1 || text.indexOf('no cut') > -1) && !vals[i].classList.contains('mw-no-cut')) {
        var cutRow = vals[i].closest('.milano-option-row');
        var cutName = cutRow ? (cutRow.getAttribute('data-option-name') || '').toLowerCase() : '';
        if (cutName.indexOf('cut') > -1 || cutName.indexOf('signature') > -1) {
          vals[i].classList.add('mw-no-cut');
          var cutParent = vals[i].parentElement;
          if (cutParent && cutParent.firstChild !== vals[i]) {
            cutParent.insertBefore(vals[i], cutParent.firstChild);
          }
          if (!vals[i].nextElementSibling || !vals[i].nextElementSibling.classList.contains('mw-cut-sep')) {
            var cutSep = document.createElement('div');
            cutSep.className = 'mw-cut-sep';
            cutSep.textContent = 'Choose a cut style:';
            vals[i].parentNode.insertBefore(cutSep, vals[i].nextSibling);
          }
        }
      }

      if ((text.indexOf('as is') > -1 || text.indexOf('as-pictured') > -1) &&
          !vals[i].classList.contains('mw-no-style') &&
          !vals[i].classList.contains('mw-no-cut')) {
        var styleSkipRow = vals[i].closest('.milano-option-row');
        var srowName = styleSkipRow ? (styleSkipRow.getAttribute('data-option-name') || '').toLowerCase() : '';
        if (srowName.indexOf('style') > -1) {
          vals[i].classList.add('mw-no-style');
          var styleSkipParent = vals[i].parentElement;
          if (styleSkipParent && styleSkipParent.firstChild !== vals[i]) {
            styleSkipParent.insertBefore(vals[i], styleSkipParent.firstChild);
          }
          if (!vals[i].nextElementSibling || !vals[i].nextElementSibling.classList.contains('mw-style-sep')) {
            var styleSep = document.createElement('div');
            styleSep.className = 'mw-style-sep';
            styleSep.textContent = 'Or choose a style:';
            vals[i].parentNode.insertBefore(styleSep, vals[i].nextSibling);
          }
        }

      } else if (!vals[i].getAttribute('data-mw-ear')) {
        var earRow = vals[i].closest('.milano-option-row');
        var earName = earRow ? (earRow.getAttribute('data-option-name') || '').toLowerCase() : '';
        if (earName.indexOf('ear') > -1) {
          vals[i].setAttribute('data-mw-ear', '1');

          if (text === 'no' || (text.indexOf('no') === 0 && text.length < 30)) {
            vals[i].classList.add('mw-no-ear');
            var earParent = vals[i].parentElement;
            if (earParent && earParent.firstChild !== vals[i]) {
              earParent.insertBefore(vals[i], earParent.firstChild);
            }
            if (!vals[i].querySelector('.mw-badge')) {
              var recBadge = document.createElement('span');
              recBadge.className = 'mw-badge';
              recBadge.textContent = 'Recommended';
              lbl.appendChild(recBadge);
            }
          }

          if (text.indexOf('yes') > -1 && text.indexOf('no') === -1 && !vals[i].querySelector('.mw-opt-inline-desc')) {
            for (var en = 0; en < lbl.childNodes.length; en++) {
              if (lbl.childNodes[en].nodeType === 3) {
                var earOrig = lbl.childNodes[en].textContent;
                if (earOrig.toLowerCase().indexOf('suggested') > -1 || earOrig.toLowerCase().indexOf('if no') > -1) {
                  lbl.childNodes[en].textContent = earOrig.replace(/\s*[-\u2013\u2014]\s*(?:Suggested|If no).*/i, '').trim();
                  break;
                }
              }
            }
            var earInline = document.createElement('span');
            earInline.className = 'mw-opt-inline-desc';
            earInline.textContent = ' \u2014 For no bio hair by ears';
            lbl.appendChild(earInline);
          }
        }

      } else if (!vals[i].getAttribute('data-mw-hairline')) {
        var hlRow = vals[i].closest('.milano-option-row');
        var hlName = hlRow ? (hlRow.getAttribute('data-option-name') || '').toLowerCase() : '';
        if (hlName.indexOf('hairline') > -1 || hlName.indexOf('blending') > -1) {
          vals[i].setAttribute('data-mw-hairline', '1');

          if (text === 'yes' && !vals[i].querySelector('.mw-opt-inline-desc')) {
            if (!vals[i].querySelector('.mw-badge')) {
              var hlBadge = document.createElement('span');
              hlBadge.className = 'mw-badge';
              hlBadge.textContent = 'Recommended';
              lbl.appendChild(hlBadge);
            }
            var hlYesInline = document.createElement('span');
            hlYesInline.className = 'mw-opt-inline-desc';
            hlYesInline.textContent = ' \u2014 I have bio hair to blend';
            lbl.appendChild(hlYesInline);
          } else if (text === 'no' && !vals[i].querySelector('.mw-opt-inline-desc')) {
            var hlNoInline = document.createElement('span');
            hlNoInline.className = 'mw-opt-inline-desc';
            hlNoInline.textContent = ' \u2014 My hairline is thinning';
            lbl.appendChild(hlNoInline);
          } else if (text.indexOf('undecided') > -1 && !vals[i].querySelector('.mw-opt-inline-desc')) {
            for (var hn = 0; hn < lbl.childNodes.length; hn++) {
              if (lbl.childNodes[hn].nodeType === 3) {
                var hlOrig = lbl.childNodes[hn].textContent;
                if (hlOrig.toLowerCase().indexOf('will contact') > -1 || hlOrig.toLowerCase().indexOf('we will') > -1 || hlOrig.toLowerCase().indexOf('or both') > -1) {
                  lbl.childNodes[hn].textContent = hlOrig.replace(/\s*[-\u2013\u2014]\s*.*/i, '').trim();
                  break;
                }
              }
            }
            var hlUndInline = document.createElement('span');
            hlUndInline.className = 'mw-opt-inline-desc';
            hlUndInline.textContent = ' \u2014 We\u2019ll reach out to help';
            lbl.appendChild(hlUndInline);
          }
        }
      }
    }

    var allRows = document.querySelectorAll('.milano-option-row');
    for (var sg = 0; sg < allRows.length; sg++) {
      var rowName = (allRows[sg].getAttribute('data-option-name') || '').toLowerCase();
      if (rowName.indexOf('position of') > -1 || rowName.indexOf('bangs or fringe') > -1) {
        allRows[sg].classList.add('mw-subgate');
        var body = allRows[sg].querySelector('.milano-option-row__body');
        if (body && !body.querySelector('.mw-subgate-label')) {
          var label = document.createElement('div');
          label.className = 'mw-subgate-label';
          if (rowName.indexOf('position') > -1) label.textContent = 'Where should framing end?';
          if (rowName.indexOf('bangs') > -1) label.textContent = 'What bang style?';
          body.insertBefore(label, body.firstChild);
        }
      }
      if (rowName.indexOf('style my') > -1 || rowName.indexOf('please style') > -1) {
        var styleBody = allRows[sg].querySelector('.milano-option-row__body');
        if (styleBody && !styleBody.querySelector('.mw-section-title')) {
          var stTitle = document.createElement('div');
          stTitle.className = 'mw-section-title';
          stTitle.textContent = 'Style';
          styleBody.insertBefore(stTitle, styleBody.firstChild);
        }
      }
      if (rowName.indexOf('location of part') > -1) {
        var partBody = allRows[sg].querySelector('.milano-option-row__body');
        if (partBody && !partBody.querySelector('.mw-section-title')) {
          var ptTitle = document.createElement('div');
          ptTitle.className = 'mw-section-title';
          ptTitle.textContent = 'Part Location';
          partBody.insertBefore(ptTitle, partBody.firstChild);
        }
      }
    }

    var prices = document.querySelectorAll('.milano-option-row__body .bold_option_value_price');
    for (var p = 0; p < prices.length; p++) {
      var pt = (prices[p].textContent || '').trim();
      if ((pt === '$0.00' || pt === '$0.00 USD' || pt === '$0') && !prices[p].querySelector('.mw-included')) {
        prices[p].innerHTML = '<span class="mw-included">Included</span>';
      }
    }
  }

  // =========================================================================
  // INLINE HELP LINKS
  // =========================================================================

  function injectInlineHelp() {
    var allRows = document.querySelectorAll('.milano-option-row');
    for (var i = 0; i < allRows.length; i++) {
      var rowName = (allRows[i].getAttribute('data-option-name') || '').toLowerCase();

      for (var h = 0; h < INLINE_HELP_MAP.length; h++) {
        if (rowName.indexOf(INLINE_HELP_MAP[h].nameMatch) === -1) continue;

        var popupId = INLINE_HELP_MAP[h].popup;
        if (!document.getElementById(popupId)) continue;

        var labelEl = allRows[i].querySelector('.mw-subgate-label') ||
                      allRows[i].querySelector('.mw-section-title');
        if (!labelEl || labelEl.querySelector('.mw-inline-help')) continue;

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mw-inline-help';
        btn.setAttribute('data-popup-id', popupId);
        btn.innerHTML =
          '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
            '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
            '<text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="10" font-weight="700" font-family="inherit">?</text>' +
          '</svg>' +
          '<span>' + INLINE_HELP_MAP[h].label + '</span>';
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          var pid = this.getAttribute('data-popup-id');
          if (pid && window.showSecurePopup) {
            window.showSecurePopup(pid);
          } else if (pid) {
            var container = document.getElementById(pid);
            if (container) {
              container.style.display = 'flex';
              document.body.classList.add('mw-popup-open');
            }
          }
        });
        labelEl.appendChild(btn);
        console.log('[Milano Wizard] Inline help added: "' + rowName + '" \u2192 #' + popupId);
        break;
      }
    }
  }

  // =========================================================================
  // STEP REASSURANCE NOTICE — Shown above Continue when paid Color/Cut
  // options are selected. Single message per step (not per-option).
  //
  // Checks if current step has STEP_REASSURANCE config AND at least one
  // paid option is selected (price > $0). Shows the notice with a
  // confidence-building message. Hides when no paid option is selected
  // or when on a step without reassurance config (Secure, Style).
  // =========================================================================

  function updateStepNotice() {
    if (!wiz.stepNoticeEl) return;

    var stepId = wiz.steps[wiz.currentStep] ? wiz.steps[wiz.currentStep].id : -1;
    var config = STEP_REASSURANCE[stepId] || null;

    if (!config) {
      wiz.stepNoticeEl.style.display = 'none';
      return;
    }

    // Check if any paid option is selected in this step's rows
    var hasPaidSelection = false;
    var step = wiz.steps[wiz.currentStep];
    for (var r = 0; r < step.rows.length; r++) {
      var row = step.rows[r];
      if (row.classList.contains('milano-option-row--hidden')) continue;

      var checked = row.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
      for (var c = 0; c < checked.length; c++) {
        var val = checked[c].closest('.bold_option_value');
        if (!val) continue;

        // Skip "no color" / "as is" / skip options
        if (val.classList.contains('mw-no-color') || val.classList.contains('mw-no-cut') || val.classList.contains('mw-no-style')) continue;

        var priceEl = val.querySelector('.bold_option_value_price');
        var priceText = priceEl ? (priceEl.textContent || '').replace(/[^\d.]/g, '') : '';
        var price = parseFloat(priceText) || 0;
        if (price > 0) { hasPaidSelection = true; break; }
      }
      if (hasPaidSelection) break;
    }

    if (hasPaidSelection) {
      wiz.stepNoticeEl.innerHTML =
        '<svg class="mw-step-notice__icon" width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
          '<rect x="2" y="4.5" width="16" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/>' +
          '<path d="M2.5 6L10 11L17.5 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '</svg>' +
        '<span class="mw-step-notice__text">' +
          '<strong>' + config.lead + '</strong> ' + config.body +
        '</span>';
      wiz.stepNoticeEl.style.display = '';
    } else {
      wiz.stepNoticeEl.style.display = 'none';
    }
  }

  // =========================================================================
  // ADDON HEADERS
  // =========================================================================

  function injectAddonHeaders() {
    var allRows = document.querySelectorAll('.milano-option-row');
    for (var i = 0; i < allRows.length; i++) {
      if (allRows[i].getAttribute('data-mw-addon-header')) continue;
      var rowName = (allRows[i].getAttribute('data-option-name') || '').toLowerCase();

      for (var a = 0; a < ADDON_HEADERS.length; a++) {
        var cfg = ADDON_HEADERS[a];
        var matches = rowName.indexOf(cfg.nameMatch) > -1;
        if (!matches && cfg.altMatch) matches = rowName.indexOf(cfg.altMatch) > -1;
        if (!matches) continue;

        var body = allRows[i].querySelector('.milano-option-row__body');
        if (!body) continue;

        allRows[i].setAttribute('data-mw-addon-header', '1');

        var header = document.createElement('div');
        header.className = 'mw-addon-header' + (cfg.warm ? ' mw-addon-header--warm' : '');

        var titleEl = document.createElement('div');
        titleEl.className = 'mw-addon-title';
        titleEl.textContent = cfg.title;

        if (cfg.helpId && document.getElementById(cfg.helpId)) {
          var helpBtn = document.createElement('button');
          helpBtn.type = 'button';
          helpBtn.className = 'mw-inline-help';
          helpBtn.setAttribute('data-popup-id', cfg.helpId);
          helpBtn.innerHTML =
            '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
              '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
              '<text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="10" font-weight="700" font-family="inherit">?</text>' +
            '</svg>' +
            '<span>' + (cfg.helpText || 'Learn more') + '</span>';
          helpBtn.addEventListener('click', function(e) {
            e.preventDefault();
            var pid = this.getAttribute('data-popup-id');
            if (pid && window.showSecurePopup) {
              window.showSecurePopup(pid);
            } else if (pid) {
              var container = document.getElementById(pid);
              if (container) {
                container.style.display = 'flex';
                document.body.classList.add('mw-popup-open');
              }
            }
          });
          titleEl.appendChild(helpBtn);
        }

        header.appendChild(titleEl);

        if (cfg.desc) {
          var descEl = document.createElement('div');
          descEl.className = 'mw-addon-desc';
          descEl.textContent = cfg.desc;
          header.appendChild(descEl);
        }

        body.insertBefore(header, body.firstChild);
        console.log('[Milano Wizard] Addon header added: "' + cfg.title + '" on "' + rowName + '"');
        break;
      }
    }
  }

  // =========================================================================
  // REORDER COLOR OPTIONS
  // =========================================================================

  var COLOR_SORT_ORDER = [
    'partial rooting',
    'full rooting',
    'mini highlight',
    'money piece',
    'highlight or lowlight',
    'lowlight',
    'toning',
    'reverse balayage',
    'balayage',
    'deux balayage',
    'custom color'
  ];

  function reorderColorOptions() {
    var colorRows = [];
    for (var i = 0; i < wiz.steps.length; i++) {
      if (wiz.steps[i].id !== 1) continue;
      colorRows = wiz.steps[i].rows;
      break;
    }
    if (colorRows.length === 0) return;

    for (var r = 0; r < colorRows.length; r++) {
      var body = colorRows[r].querySelector('.milano-option-row__body');
      if (!body) continue;
      if (body.getAttribute('data-mw-color-sorted') === '1') continue;

      var checkboxVals = body.querySelectorAll('.bold_option_value:not(.mw-no-color)');
      if (checkboxVals.length < 3) continue;

      var items = [];
      for (var v = 0; v < checkboxVals.length; v++) {
        var lbl = checkboxVals[v].querySelector('label');
        var labelText = lbl ? stripPriceText(lbl.textContent) : '';
        var sortIdx = COLOR_SORT_ORDER.length;
        var bestLen = 0;

        for (var s = 0; s < COLOR_SORT_ORDER.length; s++) {
          if (labelText.indexOf(COLOR_SORT_ORDER[s]) > -1 && COLOR_SORT_ORDER[s].length > bestLen) {
            sortIdx = s;
            bestLen = COLOR_SORT_ORDER[s].length;
          }
        }
        items.push({ el: checkboxVals[v], idx: sortIdx });
      }

      items.sort(function(a, b) { return a.idx - b.idx; });

      var sep = body.querySelector('.mw-color-sep');
      var insertAfter = sep || body.querySelector('.mw-no-color');

      for (var d = 0; d < items.length; d++) {
        if (insertAfter) {
          insertAfter.parentNode.insertBefore(items[d].el, insertAfter.nextSibling);
          insertAfter = items[d].el;
        } else {
          body.appendChild(items[d].el);
        }
      }

      body.setAttribute('data-mw-color-sorted', '1');
      console.log('[Milano Wizard] Color options reordered (' + items.length + ' items)');
    }
  }

  // =========================================================================
  // REORDER CAP / LACE
  // =========================================================================

  function reorderCapAndLace() {
    var capEl = null;
    var allVisible = document.querySelectorAll('.milano-always-visible');
    for (var i = 0; i < allVisible.length; i++) {
      var t = allVisible[i].querySelector('.bold_option_title');
      var name = t ? (t.textContent || '').trim().toLowerCase() : '';
      if (!name) name = (allVisible[i].getAttribute('data-option-name') || '').toLowerCase();
      if (name.indexOf('cap size') > -1 || name.indexOf('base size') > -1) { capEl = allVisible[i]; break; }
    }

    var laceWrapper = null, laceLabel = null;
    var labels = document.querySelectorAll('.radio__legend__label.label-typography, .select__label.label-typography');
    for (var j = 0; j < labels.length; j++) {
      var lt = (labels[j].textContent || '').trim().toLowerCase();
      if (lt.indexOf('lace front') > -1 || lt.indexOf('deep lace') > -1) {
        laceLabel = labels[j];
        laceWrapper = walkUp(labels[j], 'selector-wrapper');
        break;
      }
    }

    if (!capEl || !laceWrapper) return;

    var variantSelects = document.querySelector('variant-selects, .product__selectors');
    var insertTarget = variantSelects || laceWrapper;

    if (!(capEl.compareDocumentPosition(insertTarget) & Node.DOCUMENT_POSITION_FOLLOWING)) {
      insertTarget.parentNode.insertBefore(capEl, insertTarget);
    }

    var capTitle = capEl.querySelector('.bold_option_title');
    if (capTitle) {
      for (var c = 0; c < capTitle.childNodes.length; c++) {
        if (capTitle.childNodes[c].nodeType === 3) {
          var ct = capTitle.childNodes[c].textContent.trim();
          if (ct && ct.indexOf('Your Cap') !== 0) {
            capTitle.childNodes[c].textContent = 'Your Cap Size';
          }
          break;
        }
      }
      capTitle.innerHTML = capTitle.innerHTML.replace(/\*/g, '');

      if (!capTitle.querySelector('.mw-size-guide-native') && !capTitle.querySelector('.product__popup-toggle')) {
        var nativeBlock = document.querySelector('.product__form__size-chart');
        if (nativeBlock) {
          var nativeTrigger = nativeBlock.querySelector('button, a');
          if (nativeTrigger) {
            nativeTrigger.classList.add('mw-size-guide-native');
            capTitle.appendChild(nativeTrigger);
            nativeBlock.classList.add('mw-popup-relocated');
          }
        }
      }
    }

    if (!capEl.querySelector('.mw-cap-helper')) {
      var collKey = detectCollection();
      var capTarget = 'Medium';
      if (collKey && CAP_SIZE_MAP[collKey]) {
        capTarget = CAP_SIZE_MAP[collKey].size.charAt(0).toUpperCase() + CAP_SIZE_MAP[collKey].size.slice(1);
      }
      var capHelperText = 'Most wear ' + capTarget + '. Not sure? Size guide takes 2 min.';
      var helper = document.createElement('div');
      helper.className = 'mw-cap-helper';
      helper.textContent = capHelperText;
      capEl.appendChild(helper);
    }

    if (laceLabel) {
      for (var l = 0; l < laceLabel.childNodes.length; l++) {
        if (laceLabel.childNodes[l].nodeType === 3) {
          var llt = laceLabel.childNodes[l].textContent.trim();
          if (llt && llt.indexOf('Your Lace') !== 0) {
            laceLabel.childNodes[l].textContent = 'Your Lace Front';
          }
          break;
        }
      }

      if (!laceLabel.querySelector('.mw-lace-help')) {
        var laceHelpId = 'lace-compare';
        var laceSignal = getCollectionSignal({ rows: [] });
        for (var lm = 0; lm < LACE_POPUP_MAP.length; lm++) {
          if (LACE_POPUP_MAP[lm].pattern.test(laceSignal) && document.getElementById(LACE_POPUP_MAP[lm].popup)) {
            laceHelpId = LACE_POPUP_MAP[lm].popup;
            console.log('[Milano Wizard] Lace compare \u2192 #' + laceHelpId + ' (collection-aware)');
            break;
          }
        }
        if (laceHelpId === 'lace-compare' && !document.getElementById('lace-compare')) {
          laceHelpId = null;
        }

        if (laceHelpId) {
          var laceHelp = document.createElement('button');
          laceHelp.type = 'button';
          laceHelp.className = 'mw-lace-help';
          laceHelp.setAttribute('data-popup-id', laceHelpId);
          laceHelp.innerHTML =
            '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
              '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
              '<text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="10" font-weight="700" font-family="inherit">?</text>' +
            '</svg>' +
            '<span>Compare</span>';
          laceHelp.addEventListener('click', function(e) {
            e.preventDefault();
            var pid = this.getAttribute('data-popup-id');
            if (pid && window.showSecurePopup) {
              window.showSecurePopup(pid);
            } else if (pid) {
              var container = document.getElementById(pid);
              if (container) {
                container.style.display = 'flex';
                document.body.classList.add('mw-popup-open');
              }
            }
          });
          laceLabel.appendChild(laceHelp);
        }
      }
    }
  }

  // =========================================================================
  // GRACE COLLECTION — Help links on variant selectors
  // =========================================================================

  function addGraceHelpLinks() {
    if (!document.body.classList.contains('milano-grace')) return;

    var labels = document.querySelectorAll(
      '.radio__legend__label, .select__label, .form__label, .radio__fieldset legend span'
    );
    for (var i = 0; i < labels.length; i++) {
      var text = labels[i].textContent.trim().toLowerCase();
      if (text.indexOf('length') === -1) continue;
      if (labels[i].querySelector('.mw-lace-help')) continue;
      if (labels[i].closest('.mw-lace-help')) continue;

      var popupId = 'grace-length';
      if (!document.getElementById(popupId)) continue;

      labels[i].style.display = 'flex';
      labels[i].style.alignItems = 'center';
      labels[i].style.width = '100%';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mw-lace-help';
      btn.setAttribute('data-popup-id', popupId);
      btn.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
          '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
          '<text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="10" font-weight="700" font-family="inherit">?</text>' +
        '</svg>' +
        '<span>See lengths</span>';
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var pid = this.getAttribute('data-popup-id');
        if (pid && window.showSecurePopup) {
          window.showSecurePopup(pid);
        } else if (pid) {
          var container = document.getElementById(pid);
          if (container) {
            container.style.display = 'flex';
            document.body.classList.add('mw-popup-open');
          }
        }
      });
      labels[i].appendChild(btn);
      console.log('[Milano Wizard] Grace length help added');
    }
  }

  // =========================================================================
  // VARIANT CHANGE SURVIVAL
  // =========================================================================

  function watchVariantChanges() {
    var selectors = document.querySelector('variant-selects, .product__selectors');
    if (!selectors || !selectors.parentElement) return;

    var weAreWriting = false;
    var debounce = null;
    var obs = new MutationObserver(function() {
      if (weAreWriting) return;
      if (debounce) clearTimeout(debounce);
      selectors.style.opacity = '0';
      selectors.style.transition = 'opacity 0.05s';
      debounce = setTimeout(function() {
        weAreWriting = true;
        reorderCapAndLace();
        addGraceHelpLinks();
        injectLaceSubtitles();
        selectors.style.opacity = '1';
        setTimeout(function() { weAreWriting = false; }, 150);
      }, 50);
    });
    obs.observe(selectors.parentElement, { childList: true, subtree: true });
    wiz.observers.push(obs);
  }

  // =========================================================================
  // SKIN → SILK CORRECTION
  // =========================================================================

  function fixSkinToSilk() {
    var targets = document.querySelectorAll(
      '.milano-details-content, .milano-construction-legacy, .milano-specs-table, ' +
      '.milano-product-description, .product__description, [data-product-description]'
    );
    if (targets.length === 0) return;
    var fixed = 0;

    for (var i = 0; i < targets.length; i++) {
      var el = targets[i];
      var html = el.innerHTML;
      var updated = html
        .replace(/\bSkin\s+Part\b/g, 'Silk Part')
        .replace(/\bskin\s+part\b/g, 'silk part')
        .replace(/\bSKIN\s+PART\b/g, 'SILK PART')
        .replace(/\bSkin\s+Top\b/g, 'Silk Top')
        .replace(/\bskin\s+top\b/g, 'silk top')
        .replace(/\bSKIN\s+TOP\b/g, 'SILK TOP')
        .replace(/\bSkin\s+Part\s+Wig\b/g, 'Silk Part Wig')
        .replace(/\bSkin\s+Top\s+Wig\b/g, 'Silk Top Wig')
        .replace(/\bSkin\s+Top\s+Density\b/g, 'Silk Top Density')
        .replace(/\bskin\s+top\s+density\b/gi, 'Silk Top Density');
      if (updated !== html) {
        el.innerHTML = updated;
        fixed++;
      }
    }
    if (fixed > 0) console.log('[Milano Wizard] Skin\u2192Silk corrected in ' + fixed + ' element(s)');
  }

  // =========================================================================
  // INJECT ELEMENTS
  // =========================================================================

  function findATCArea() {
    var form = document.querySelector('.product__form');
    return form ? form.querySelector('.product__submit__buttons, .product__submit__holder, .product__submit') : document.querySelector('.product__submit__buttons, .product__submit__holder');
  }

  function injectConsultCTA() {
    var atc = findATCArea();
    if (!atc || document.querySelector('.mw-consult')) return;
    var el = document.createElement('div');
    el.className = 'mw-consult';
    el.innerHTML = '<div class="mw-consult__title">Not sure? We\'ll help you choose.</div><p class="mw-consult__text">Our specialists guide women through this every day \u2014 with patience, privacy, and zero pressure.</p><div class="mw-consult__btns"><a href="/pages/contact-us" target="_blank" rel="noopener" class="mw-consult__btn mw-consult__btn--primary">\uD83D\uDCAC Contact Us</a><a href="/pages/consultations" target="_blank" rel="noopener" class="mw-consult__btn mw-consult__btn--secondary">\uD83D\uDCC5 Free Consult</a></div>';
    atc.parentNode.insertBefore(el, atc.nextSibling);
  }

  // =========================================================================
  // CLEANUP
  // =========================================================================

  function cleanup() {
    document.body.classList.remove('milano-wizard-mode');
    document.body.classList.remove('milano-flat-options');
    var flatStyles = document.getElementById('mw-flat-styles');
    if (flatStyles) flatStyles.remove();
    var mobileStyles = document.getElementById('mw-mobile-styles');
    if (mobileStyles) mobileStyles.remove();
    wiz.active = false;
    for (var k = 0; k < wiz.intervals.length; k++) {
      if (typeof wiz.intervals[k] === 'object' && wiz.intervals[k].stop) wiz.intervals[k].stop();
      else clearInterval(wiz.intervals[k]);
    }
    wiz.intervals = [];
    for (var o = 0; o < wiz.observers.length; o++) wiz.observers[o].disconnect();
    wiz.observers = [];
    var rows = document.querySelectorAll('.milano-option-row');
    for (var i = 0; i < rows.length; i++) rows[i].style.display = '';
    var injected = document.querySelectorAll('.mw-indicator-row, .mw-hint-wrap, .mw-nav, .mw-consult, .mw-opt-inline-desc, .mw-lace-sub, .mw-lace-help, .mw-inline-help, .mw-badge, .mw-included, .mw-review-card, .mw-toggle-total, .mw-wiz-total, .mw-price-context, .mw-cap-helper, .mw-subgate-label, .mw-section-title, .mw-color-sep, .mw-cut-sep, .mw-style-sep, .mw-step-notice');
    for (var j = 0; j < injected.length; j++) if (injected[j].parentNode) injected[j].parentNode.removeChild(injected[j]);
    var priceEnhanced = document.querySelectorAll('.mw-price-enhanced');
    for (var pe = 0; pe < priceEnhanced.length; pe++) priceEnhanced[pe].classList.remove('mw-price-enhanced');
    var relocated = document.querySelector('.mw-size-guide-native');
    var origBlock = document.querySelector('.mw-popup-relocated');
    if (relocated && origBlock) {
      relocated.classList.remove('mw-size-guide-native');
      origBlock.appendChild(relocated);
      origBlock.classList.remove('mw-popup-relocated');
    }
    var ncs = document.querySelectorAll('.mw-no-color');
    for (var n = 0; n < ncs.length; n++) ncs[n].classList.remove('mw-no-color');
    var ncuts = document.querySelectorAll('.mw-no-cut');
    for (var nc = 0; nc < ncuts.length; nc++) ncuts[nc].classList.remove('mw-no-cut');
    var nstyles = document.querySelectorAll('.mw-no-style');
    for (var ns = 0; ns < nstyles.length; ns++) nstyles[ns].classList.remove('mw-no-style');
    var nears = document.querySelectorAll('.mw-no-ear');
    for (var ne = 0; ne < nears.length; ne++) nears[ne].classList.remove('mw-no-ear');
    var earMarked = document.querySelectorAll('[data-mw-ear]');
    for (var em = 0; em < earMarked.length; em++) earMarked[em].removeAttribute('data-mw-ear');
    var hlMarked = document.querySelectorAll('[data-mw-hairline]');
    for (var hm = 0; hm < hlMarked.length; hm++) hlMarked[hm].removeAttribute('data-mw-hairline');
    var groupSeps = document.querySelectorAll('.mw-step-group-sep');
    for (var gs = 0; gs < groupSeps.length; gs++) groupSeps[gs].classList.remove('mw-step-group-sep');
    var addonHeaders = document.querySelectorAll('.mw-addon-header');
    for (var ah = 0; ah < addonHeaders.length; ah++) if (addonHeaders[ah].parentNode) addonHeaders[ah].parentNode.removeChild(addonHeaders[ah]);
    var addonMarked = document.querySelectorAll('[data-mw-addon-header]');
    for (var am = 0; am < addonMarked.length; am++) addonMarked[am].removeAttribute('data-mw-addon-header');
    var sorted = document.querySelectorAll('[data-mw-color-sorted]');
    for (var cs = 0; cs < sorted.length; cs++) sorted[cs].removeAttribute('data-mw-color-sorted');
    var sgs = document.querySelectorAll('.mw-subgate');
    for (var g = 0; g < sgs.length; g++) sgs[g].classList.remove('mw-subgate');
    var renamed = document.querySelectorAll('[data-mw-renamed]');
    for (var rm = 0; rm < renamed.length; rm++) renamed[rm].removeAttribute('data-mw-renamed');
    var hasServices = document.querySelector('.mw-has-services');
    if (hasServices) hasServices.classList.remove('mw-has-services');
    wiz.collectionSignal = '';
  }

  // =========================================================================
  // BOOT
  // =========================================================================
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  setTimeout(fixSkinToSilk, 1500);
})();